<template>
  <div
    class="alice-chat"
    :class="{
      'alice-chat--insights-collapsed': insightsCollapsed,
      'alice-chat--has-index': conversationIndex.length > 0,
    }"
    @dragenter="onDragEnter"
    @dragover="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop"
  >
    <div v-if="dragDepth > 0" class="alice-chat__dropzone" aria-hidden="true">
      <span class="alice-chat__dropzone-inner">
        <Icon name="image" :size="18" />
        Drop files to attach
      </span>
    </div>

    <InsightDialog
      :dialog="dialog"
      :insight="selectedInsight"
      :project-uid="project?.uid || ''"
      :chapter-title="chapters[selectedChapter]?.title || ''"
      :chapter-index="selectedChapter"
      @close="closeDialog"
      @delete="removeInsight"
      @save="updateInsight"
    />

    <SaveNoteDialog
      :dialog="saveDialog"
      :note="editingSavedNote"
      :draft="saveDraft"
      :existing-labels="savedLabels"
      @close="closeSaveDialog"
      @save="onSaveNote"
    />

    <!-- Floating "Save as note" button shown next to a text selection -->
    <button
      v-if="selectionButton.show"
      class="alice-chat__selection-save"
      type="button"
      :style="{
        left: selectionButton.x + 'px',
        top: selectionButton.y + 'px',
      }"
      @mousedown.prevent
      @click="saveSelectionAsNote"
    >
      <Icon name="bookmark" :size="13" />
      Bewaar als notitie
    </button>

    <!-- ======================== Center column ======================== -->
    <section class="alice-chat__main">
      <ChatIndexRail
        :entries="indexEntries"
        :active-key="indexActiveKey"
        @select="onIndexSelect"
      />
      <header class="alice-chat__header">
        <h1 class="alice-chat__title">Chat</h1>

        <div class="alice-chat__mode" role="tablist" aria-label="Chat mode">
          <button
            type="button"
            role="tab"
            class="alice-chat__mode-btn"
            :class="{ 'alice-chat__mode-btn--active': chatMode === 'personal' }"
            :aria-selected="chatMode === 'personal'"
            @click="chatMode = 'personal'"
          >
            <Icon name="lock" :size="13" />
            <span>Personal</span>
          </button>
          <button
            type="button"
            role="tab"
            class="alice-chat__mode-btn"
            :class="{ 'alice-chat__mode-btn--active': chatMode === 'communal' }"
            :aria-selected="chatMode === 'communal'"
            @click="chatMode = 'communal'"
          >
            <Icon name="users" :size="13" />
            <span>Team</span>
          </button>
        </div>

        <v-menu>
          <template #activator="{ props: menuProps }">
            <button
              v-bind="menuProps"
              class="alice-chat__model alice-chat__model--btn"
              aria-label="Model"
            >
              <span class="alice-chat__model-control">
                <span>{{ modelLabel }}</span>
                <span
                  v-if="currentModel"
                  class="alice-chat__eco-badge"
                  :class="`alice-chat__eco-badge--${currentModel.eco}`"
                  :title="`${currentModel.ecoLabel} energy use`"
                >
                  <Icon name="leaf" :size="12" />
                </span>
                <Icon name="chevron-down" :size="14" />
              </span>
            </button>
          </template>
          <v-list class="alice-menu-list alice-chat__model-menu">
            <v-list-item
              v-for="opt in modelOptions"
              :key="opt.value"
              :active="opt.value === model"
              class="alice-chat__model-item"
              @click="model = opt.value"
            >
              <div class="alice-chat__model-row">
                <span class="alice-chat__model-name">{{ opt.title }}</span>
                <span class="alice-chat__model-badges">
                  <span
                    class="alice-chat__eco-badge"
                    :class="`alice-chat__eco-badge--${opt.eco}`"
                  >
                    <Icon name="leaf" :size="12" />
                    <span>{{ opt.ecoLabel }}</span>
                  </span>
                </span>
              </div>
            </v-list-item>
            <div class="alice-chat__eco-note">
              <span class="alice-chat__eco-note-icon">
                <Icon name="leaf" :size="14" />
              </span>
              <span class="alice-chat__eco-note-text">
                Choosing a lighter model for simpler tasks helps reduce energy
                use and environmental impact.
                <a
                  :href="ecoLearnMoreUrl"
                  target="_blank"
                  rel="noopener"
                  @click.stop
                  >Learn more</a
                >
              </span>
            </div>
          </v-list>
        </v-menu>

        <label v-if="sessionList.length > 1" class="alice-chat__model">
          <span class="alice-chat__model-label">Session</span>
          <span class="alice-chat__model-control">
            <select
              :value="currentViewingVersion"
              class="alice-chat__model-select"
              @change="onSessionChange($event.target.value)"
            >
              <option
                v-for="s in sessionList"
                :key="s.version"
                :value="s.version"
              >
                {{ s.label || `Session ${s.version}`
                }}{{
                  s.version === currentActiveVersion
                    ? " (current)"
                    : " (archived)"
                }}
              </option>
            </select>
            <Icon name="chevron-down" :size="14" />
          </span>
        </label>

        <div
          v-if="chatMode === 'communal' && onlineCount"
          class="alice-chat__presence"
          :title="`${onlineNames} ${
            onlineCount === 1 ? 'is' : 'are'
          } also here`"
        >
          <span class="alice-chat__presence-dot" />
          <span class="alice-chat__presence-stack">
            <Avatar
              v-for="u in onlineUsers.slice(0, 3)"
              :key="u.userUid"
              class="alice-chat__presence-avatar"
              :name="u.userName"
              :photo="u.userPhoto"
              :color="u.color"
              :size="24"
              ring
            />
            <span v-if="onlineCount > 3" class="alice-chat__presence-more"
              >+{{ onlineCount - 3 }}</span
            >
          </span>
          <span class="alice-chat__presence-label"
            >{{ onlineCount }} online</span
          >
        </div>

        <div class="alice-chat__spacer" />

        <button
          v-if="replyAttention.supported"
          type="button"
          class="alice-chat__notify"
          :class="{ 'alice-chat__notify--on': replyNotifyOn }"
          :aria-pressed="replyNotifyOn"
          :aria-label="replyNotifyTitle"
          :title="replyNotifyTitle"
          @click="toggleReplyNotifications"
        >
          <Icon
            :name="replyAttention.permission === 'denied' ? 'bell-off' : 'bell'"
            :size="16"
          />
        </button>

        <v-menu v-if="isViewingActiveSession">
          <template #activator="{ props: menuProps }">
            <button v-bind="menuProps" class="alice-chat__clear">
              <Icon name="more" :size="16" />
              <span>Chat options</span>
              <Icon name="chevron-down" :size="14" />
            </button>
          </template>
          <v-list class="alice-menu-list">
            <v-list-item
              v-if="chatMode === 'personal'"
              :disabled="compressing || renderableMessages.length < 2"
              @click="compressActiveChat"
            >
              <v-list-item-title>{{
                compressing ? "Compressing…" : "Compress"
              }}</v-list-item-title>
              <v-list-item-subtitle>{{
                renderableMessages.length < 2
                  ? "Send a few more messages before compressing"
                  : "Summarize this session and start a new one"
              }}</v-list-item-subtitle>
            </v-list-item>
            <v-list-item v-if="chatMode === 'personal'" @click="clearChat">
              <v-list-item-title>Clear chat</v-list-item-title>
              <v-list-item-subtitle
                >Delete all messages in this chat</v-list-item-subtitle
              >
            </v-list-item>
            <v-list-item
              v-if="chatMode === 'communal'"
              :disabled="
                compressingCommunal ||
                clearingCommunal ||
                isAnswering ||
                communalRenderable.length < 2
              "
              @click="compressCommunalChat"
            >
              <v-list-item-title>{{
                compressingCommunal ? "Compressing…" : "Compress"
              }}</v-list-item-title>
              <v-list-item-subtitle>{{
                isAnswering
                  ? "Wait until Alice has finished answering"
                  : communalRenderable.length < 2
                  ? "Send a few more messages before compressing"
                  : "Summarize the team session and start a new one for everyone"
              }}</v-list-item-subtitle>
            </v-list-item>
            <v-list-item
              v-if="chatMode === 'communal'"
              :disabled="
                isAnswering ||
                clearingCommunal ||
                compressingCommunal ||
                !communalRenderable.length
              "
              @click="clearCommunalChat"
            >
              <v-list-item-title>{{
                clearingCommunal ? "Clearing…" : "Clear chat"
              }}</v-list-item-title>
              <v-list-item-subtitle>{{
                isAnswering
                  ? "Wait until Alice has finished answering"
                  : "Delete the current team session for everyone in this project"
              }}</v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </v-menu>

        <button
          class="alice-chat__insights-toggle"
          type="button"
          :aria-label="insightsCollapsed ? 'Show insights' : 'Hide insights'"
          @click="toggleInsights"
        >
          <Icon
            :name="insightsCollapsed ? 'chevron-left' : 'chevron-right'"
            :size="14"
          />
        </button>
      </header>

      <div
        ref="bodyRef"
        class="alice-chat__body chat-container"
        @mouseup="handleTextSelection"
        @scroll="onBodyScroll"
      >
        <div
          v-if="contextEntries.length"
          class="alice-chat__context"
          :class="{ 'alice-chat__context--open': contextExpanded }"
          aria-label="Context"
        >
          <button
            type="button"
            class="alice-chat__context-head"
            :aria-expanded="contextExpanded"
            @click="contextExpanded = !contextExpanded"
          >
            <Icon name="folder" :size="14" />
            <span
              >{{ enabledContextCount }} of {{ contextEntries.length }} sources
              sent to context</span
            >
            <Icon
              name="chevron-right"
              :size="14"
              class="alice-chat__context-chevron"
            />
          </button>
          <div class="alice-chat__context-collapse">
            <div class="alice-chat__context-clip">
              <div class="alice-chat__context-list">
                <div class="alice-chat__context-toolbar">
                  <button
                    type="button"
                    class="alice-chat__context-bulk"
                    :disabled="enabledContextCount === contextEntries.length"
                    @click="setAllContext(true)"
                  >
                    Select all
                  </button>
                  <span class="alice-chat__context-bulk-sep">·</span>
                  <button
                    type="button"
                    class="alice-chat__context-bulk"
                    :disabled="enabledContextCount === 0"
                    @click="setAllContext(false)"
                  >
                    Deselect all
                  </button>
                </div>
                <label
                  v-for="entry in contextEntries"
                  :key="entry.key"
                  class="alice-chat__context-item"
                  :class="{ 'alice-chat__context-item--off': !entry.enabled }"
                  :title="entry.hint || null"
                >
                  <input
                    type="checkbox"
                    class="alice-chat__context-check"
                    :checked="entry.enabled"
                    @change="toggleContextItem(entry)"
                  />
                  <span class="alice-chat__context-name">{{ entry.name }}</span>
                  <span
                    v-if="entry.tags && entry.tags.length"
                    class="alice-chat__context-tags"
                  >
                    <span
                      v-for="(t, ti) in entry.tags"
                      :key="ti"
                      class="alice-chat__context-tag"
                    >
                      <span class="alice-chat__context-tag-key">{{
                        t.key
                      }}</span>
                      {{ t.value }}
                    </span>
                  </span>
                  <span class="alice-chat__context-type">{{ entry.type }}</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div v-if="chatLoading" class="alice-chat__status">
          <ThinkingDots :size="6" />
          <span>Loading chat…</span>
        </div>
        <div
          v-else-if="chatStartError"
          class="alice-chat__status alice-chat__status--error"
        >
          <strong>Could not load chat</strong>
          <span>{{ chatStartError }}</span>
        </div>

        <div
          v-if="!hasMessages && !chatLoading && !chatStartError"
          class="alice-chat__welcome"
        >
          <div class="alice-chat__welcome-mark">
            <VP1Pattern :size="28" :opacity="0.5" />
          </div>
          <h2 class="alice-chat__welcome-title">
            Hey {{ userFirstName }}, what can I help you with today?
          </h2>
          <p class="alice-chat__welcome-sub">
            Ask anything about this project — type below to get started.
          </p>
        </div>

        <template v-for="(m, i) in visibleMessages" :key="i">
          <article
            v-if="m.role === 'user'"
            class="alice-chat__msg alice-chat__msg--user"
            :data-msg-index="i"
          >
            <div class="alice-chat__msg-attr">
              <span class="alice-chat__msg-time">{{
                formatTime(m.timestamp)
              }}</span>
              <span class="alice-chat__msg-name">
                {{ msgAuthorFirst(m).toUpperCase()
                }}<template v-if="msgIsMine(m)"> · YOU</template>
              </span>
              <Avatar
                :name="msgAuthorName(m)"
                :photo="msgAuthorPhoto(m)"
                :size="28"
                :color="msgAuthorColor(m)"
              />
            </div>
            <div class="alice-chat__bubble alice-chat__bubble--user">
              {{ messageText(m) }}
              <div
                v-if="
                  chatMode === 'communal' && m.status && m.status !== 'answered'
                "
                class="alice-chat__msg-status"
                :class="{
                  'alice-chat__msg-status--error': m.status === 'error',
                }"
              >
                <ThinkingDots
                  v-if="m.status === 'queued' || m.status === 'answering'"
                  :size="4"
                />
                <span v-if="m.status === 'queued'">In queue…</span>
                <span v-else-if="m.status === 'answering'">Answering…</span>
                <span v-else-if="m.status === 'error'"
                  >⚠ {{ m.error || "Failed to answer" }}</span
                >
              </div>
              <div
                v-if="m.attachments && m.attachments.length"
                class="alice-chat__msg-attachments"
              >
                <a
                  v-for="(att, ai) in m.attachments"
                  :key="ai"
                  :href="att.url"
                  target="_blank"
                  rel="noopener"
                  class="alice-chat__msg-attachment"
                >
                  <Icon
                    :name="
                      (att.contentType || '').startsWith('image/')
                        ? 'image'
                        : 'file'
                    "
                    :size="12"
                  />
                  <span>{{ att.name }}</span>
                </a>
              </div>
            </div>
          </article>

          <article
            v-else
            class="alice-chat__msg alice-chat__msg--ai"
            :data-msg-index="i"
          >
            <div class="alice-chat__msg-attr">
              <span class="alice-chat__msg-mark" />
              <span class="alice-chat__msg-name">
                ALICE · {{ msgModelLabel(m).toUpperCase()
                }}<template v-if="chatMode === 'communal' && m.authorName">
                  · for {{ msgAuthorFirst(m) }}</template
                >
              </span>
              <span
                v-if="m.skillMeta && m.skillMeta.length"
                class="alice-chat__msg-skill"
                :title="
                  m.skillMeta.map((s) => `${s.name} v${s.version}`).join(', ')
                "
              >
                <Icon name="book" :size="10" />
                {{ m.skillMeta[0].name
                }}<template v-if="m.skillMeta.length > 1">
                  +{{ m.skillMeta.length - 1 }}</template
                >
              </span>
              <!-- Past research consulted for this answer (researchMeta). -->
              <button
                v-if="researchSourceCount(m)"
                type="button"
                class="alice-chat__msg-skill alice-chat__msg-research"
                :class="{
                  'alice-chat__msg-research--open':
                    researchOpenFor === researchKey(m, i),
                }"
                :aria-expanded="
                  researchOpenFor === researchKey(m, i) ? 'true' : 'false'
                "
                :title="researchChipTitle(m.researchMeta)"
                @click="toggleResearch(m, i)"
              >
                <Icon name="search" :size="10" />
                {{ researchSourceCount(m) }}
                {{ researchSourceCount(m) === 1 ? "bron" : "bronnen" }}
              </button>
              <span class="alice-chat__msg-time">{{
                formatTime(m.timestamp)
              }}</span>
            </div>
            <ul
              v-if="
                researchSourceCount(m) && researchOpenFor === researchKey(m, i)
              "
              class="alice-chat__msg-sources"
            >
              <li
                v-for="s in m.researchMeta.sources"
                :key="s.kind + ':' + s.id"
              >
                <router-link
                  v-if="s.kind === 'app'"
                  :to="`/project/${s.id}/insights`"
                  >{{ sourceLabel(s) }}</router-link
                >
                <a
                  v-else-if="s.url"
                  :href="s.url"
                  target="_blank"
                  rel="noopener"
                  >{{ sourceLabel(s) }} <Icon name="external" :size="10"
                /></a>
                <span v-else>{{ sourceLabel(s) }}</span>
                <span class="alice-chat__msg-source-meta">{{
                  s.kind === "archive" ? "Archief" : "Alice"
                }}</span>
              </li>
            </ul>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div class="alice-chat__ai" v-html="renderMarkdown(m.content)" />
            <div class="alice-chat__msg-actions">
              <button
                class="alice-chat__add-insight-link"
                type="button"
                title="Bewaar dit antwoord (of je selectie) voor later"
                @click="startSaveFromMessage(m, $event)"
              >
                <Icon name="bookmark" :size="12" />
                Bewaar
              </button>
              <v-menu v-if="!$store.getters.isGuest">
                <template #activator="{ props: menuProps }">
                  <button
                    v-bind="menuProps"
                    class="alice-chat__add-insight-link"
                    type="button"
                    :disabled="formattingInsight"
                  >
                    <ThinkingDots v-if="formattingInsight" :size="4" />
                    <Icon v-else name="plus" :size="12" />
                    {{
                      formattingInsight
                        ? "Formatting insight…"
                        : "Add insight from this answer"
                    }}
                  </button>
                </template>
                <v-list class="alice-menu-list">
                  <v-list-item
                    v-for="(c, ci) in project?.chapters || []"
                    :key="c.uid || ci"
                    @click="addInsight(ci, m.content)"
                  >
                    <v-list-item-title>{{
                      c.title || `Chapter ${ci + 1}`
                    }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="startInsightChapter(i)">
                    <v-list-item-title>+ New chapter…</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
            <form
              v-if="addingInsightChapterFor === i"
              class="alice-chat__composer alice-chat__composer--msg"
              @submit.prevent="submitInsightChapter(m)"
            >
              <input
                :ref="setInsightChapterInput"
                v-model="insightChapterName"
                class="alice-chat__composer-input"
                type="text"
                placeholder="New chapter name (e.g. Onboarding)"
                maxlength="60"
                @keydown.esc.prevent="cancelInsightChapter"
              />
              <button
                class="alice-chat__composer-create"
                type="submit"
                :disabled="!insightChapterName.trim()"
              >
                Create
              </button>
              <button
                class="alice-chat__composer-cancel"
                type="button"
                title="Cancel"
                @click="cancelInsightChapter"
              >
                ×
              </button>
            </form>
          </article>
        </template>

        <article
          v-if="awaitingReply || (chatMode === 'communal' && isAnswering)"
          class="alice-chat__msg alice-chat__msg--ai alice-chat__msg--pending"
          aria-live="polite"
        >
          <div class="alice-chat__msg-attr">
            <span class="alice-chat__msg-mark" />
            <span class="alice-chat__msg-name"
              >ALICE · {{ pendingModelLabel.toUpperCase() }}</span
            >
            <span
              v-if="chatMode === 'communal' && communalQueueCount"
              class="alice-chat__msg-time"
              >{{ communalQueueCount }} in queue</span
            >
          </div>
          <div class="alice-chat__ai alice-chat__ai--pending">
            <ThinkingDots :size="6" />
          </div>
        </article>

        <div v-if="notice" class="alice-chat__notice" role="status">
          <span class="alice-chat__notice-mark" />
          <span class="alice-chat__notice-text">{{ notice }}</span>
          <button
            type="button"
            class="alice-chat__notice-x"
            title="Dismiss"
            @click="notice = ''"
          >
            ×
          </button>
        </div>

        <div v-if="skillNotice" class="alice-chat__notice" role="status">
          <span class="alice-chat__notice-mark" />
          <span class="alice-chat__notice-text">{{ skillNotice }}</span>
          <button
            type="button"
            class="alice-chat__notice-x"
            title="Dismiss"
            @click="skillNotice = ''"
          >
            ×
          </button>
        </div>

        <div v-if="rulebookNotice" class="alice-chat__notice" role="status">
          <span class="alice-chat__notice-mark" />
          <span class="alice-chat__notice-text">
            Alice will remember: “{{ rulebookNotice.text }}” — applied to your
            personal chats from now on.
          </span>
          <button
            type="button"
            class="alice-chat__notice-undo"
            @click="undoRulebookRule"
          >
            Undo
          </button>
          <button
            type="button"
            class="alice-chat__notice-x"
            title="Dismiss"
            @click="rulebookNotice = null"
          >
            ×
          </button>
        </div>

        <div
          v-if="skillFeedbackNotice"
          class="alice-chat__notice"
          role="status"
        >
          <span class="alice-chat__notice-mark" />
          <span class="alice-chat__notice-text">
            Sent your feedback on “{{ skillFeedbackNotice.title }}” to the
            skill's owner.
          </span>
          <router-link
            class="alice-chat__notice-undo"
            :to="{
              path: `/skills/${skillFeedbackNotice.skillId}`,
              query: { tab: 'feedback' },
            }"
            >View</router-link
          >
          <button
            type="button"
            class="alice-chat__notice-x"
            title="Dismiss"
            @click="skillFeedbackNotice = null"
          >
            ×
          </button>
        </div>
      </div>

      <footer
        v-if="!isViewingActiveSession"
        class="alice-chat__input-area alice-chat__input-area--archived"
      >
        <div class="alice-chat__archived-banner">
          <Icon name="lock" :size="14" />
          <span
            >Read-only — archived session. Switch to the current session to send
            new messages.</span
          >
        </div>
      </footer>

      <footer v-else class="alice-chat__input-area">
        <div
          v-if="pendingAttachments.length"
          class="alice-chat__pending"
          aria-label="Attachments to send"
        >
          <span
            v-for="(att, idx) in pendingAttachments"
            :key="idx"
            class="alice-chat__pending-chip"
            :class="{
              'alice-chat__pending-chip--uploading': uploadingAttachments,
            }"
          >
            <img
              v-if="att.previewUrl"
              :src="att.previewUrl"
              class="alice-chat__pending-chip-thumb"
              alt=""
            />
            <Icon
              v-else
              :name="att.contentType.startsWith('image/') ? 'image' : 'file'"
              :size="12"
            />
            <span class="alice-chat__pending-chip-name">{{ att.name }}</span>
            <button
              v-if="!uploadingAttachments"
              type="button"
              class="alice-chat__pending-chip-x"
              :title="`Remove ${att.name}`"
              @click="removePending(idx)"
            >
              ×
            </button>
            <span
              v-if="uploadingAttachments"
              class="alice-chat__pending-chip-progress"
              :style="{ width: Math.round((att.progress || 0) * 100) + '%' }"
            />
          </span>
        </div>
        <div
          v-if="activeSkills.length"
          class="alice-chat__skills"
          aria-label="Active skills"
        >
          <span
            v-for="s in activeSkills"
            :key="s.id"
            class="alice-chat__skill-chip"
            :title="s.description"
          >
            <Icon name="book" :size="12" />
            <span class="alice-chat__skill-chip-name">{{ s.title }}</span>
            <button
              type="button"
              class="alice-chat__skill-chip-x"
              :title="`Deactivate ${s.title}`"
              @click="unpinSkill(s.id)"
            >
              ×
            </button>
          </span>
        </div>
        <input
          ref="fileInput"
          type="file"
          multiple
          accept="application/pdf,image/png,image/jpeg,image/webp,image/gif"
          class="alice-chat__file-input"
          @change="onFilePick"
        />
        <div
          v-if="chatMode === 'communal' && lockedByOther"
          class="alice-chat__typing-banner"
          aria-live="polite"
        >
          <ThinkingDots :size="5" />
          <span>{{ inputPlaceholder }}</span>
        </div>
        <div class="alice-chat__input-bar">
          <div
            v-if="slashOpen"
            ref="slashListRef"
            class="alice-chat__slash"
            role="listbox"
            aria-label="Pick a skill"
          >
            <div v-if="!slashResults.length" class="alice-chat__slash-empty">
              No skills match “{{ slashQuery }}” — Enter sends it as a message
            </div>
            <button
              v-for="(s, i) in slashResults"
              :key="s.id"
              type="button"
              class="alice-chat__slash-item"
              :class="{ 'is-active': i === slashIndex }"
              role="option"
              :aria-selected="i === slashIndex"
              @mousedown.prevent
              @mousemove="slashIndex = i"
              @click="selectSlashSkill(s)"
            >
              <span class="alice-chat__slash-title">{{ s.title }}</span>
              <span class="alice-chat__slash-name">/{{ s.name }}</span>
              <span class="alice-chat__slash-desc">{{ s.description }}</span>
            </button>
          </div>
          <button
            type="button"
            class="alice-chat__attach"
            :disabled="inputDisabled"
            title="Attach files (PDF or image) to this message"
            @click="openFilePicker"
          >
            <Icon name="paperclip" :size="18" />
          </button>
          <button
            v-if="voiceSupported && voiceState !== 'recording'"
            type="button"
            class="alice-chat__attach"
            :disabled="micDisabled"
            :title="
              voiceState === 'transcribing'
                ? 'Transcribing…'
                : voiceLastBlob
                ? 'Retry transcribing your last recording'
                : 'Record a voice message (English) — click again to stop and send'
            "
            @click="toggleMic"
          >
            <ThinkingDots v-if="voiceState === 'transcribing'" :size="4" />
            <Icon v-else name="mic" :size="18" />
          </button>
          <template v-else-if="voiceSupported">
            <button
              type="button"
              class="alice-chat__attach alice-chat__attach--recording"
              title="Stop and send"
              @click="toggleMic"
            >
              <Icon name="stop" :size="16" :filled="true" />
            </button>
            <span class="alice-chat__rec" role="status" aria-live="polite">
              <span class="alice-chat__rec-dot" />
              <span class="alice-chat__rec-time">{{ voiceElapsedLabel }}</span>
              <button
                type="button"
                class="alice-chat__rec-x"
                title="Cancel recording (Esc)"
                @click="stopVoiceRecording(true)"
              >
                ×
              </button>
            </span>
          </template>
          <textarea
            v-model="input"
            class="alice-chat__textarea"
            rows="1"
            :placeholder="inputPlaceholder"
            :disabled="inputDisabled"
            @keydown="handleKeydown"
            @input="autoSizeTextarea"
            @paste="handlePaste"
            ref="textareaRef"
          />
          <button class="alice-chat__send" :disabled="!canSend" @click="send">
            <template v-if="uploadingAttachments">
              <ThinkingDots :size="4" />
              <span>Uploading…</span>
            </template>
            <template v-else-if="chatMode === 'personal' && awaitingReply">
              <ThinkingDots :size="4" />
              <span>Thinking…</span>
            </template>
            <template v-else>
              <span>{{ sendButtonLabel }}</span>
              <Icon name="arrow-right" :size="16" />
            </template>
          </button>
        </div>
        <div class="alice-chat__input-helper">
          <span
            >{{ enabledContextCount }} of {{ contextEntries.length }} sources
            <template v-if="totalQuotes">
              · {{ totalQuotes.toLocaleString("en-GB") }} quotes
            </template>
            in context</span
          >
        </div>
      </footer>
    </section>

    <!-- ======================== Right insights ======================== -->
    <aside class="alice-chat__insights" aria-label="Insights">
      <div class="alice-chat__insights-pattern" aria-hidden="true">
        <VP1Pattern :size="64" :opacity="0.06" />
      </div>
      <div class="alice-chat__insights-inner">
        <header class="alice-chat__insights-head">
          <div class="alice-chat__panel-tabs">
            <button
              class="alice-chat__panel-tab"
              :class="{
                'alice-chat__panel-tab--active': insightPanel === 'insights',
              }"
              type="button"
              @click="insightPanel = 'insights'"
            >
              Inhoud
              <span v-if="insightCount" class="alice-chat__chip-tab-count">{{
                insightCount
              }}</span>
            </button>
            <button
              class="alice-chat__panel-tab"
              :class="{
                'alice-chat__panel-tab--active': insightPanel === 'saved',
              }"
              type="button"
              @click="insightPanel = 'saved'"
            >
              Notes
              <span
                v-if="savedNotes.length"
                class="alice-chat__chip-tab-count"
                >{{ savedNotes.length }}</span
              >
            </button>
          </div>
          <div class="alice-chat__insights-spacer" />
          <button
            v-if="insightPanel === 'insights'"
            class="alice-chat__insights-add"
            :title="addingChapter ? 'Cancel new chapter' : 'Add chapter'"
            :class="{ 'alice-chat__insights-add--active': addingChapter }"
            @click="toggleAddChapter"
          >
            <Icon name="plus" :size="16" />
          </button>
        </header>

        <template v-if="insightPanel === 'insights'">
          <div class="alice-chat__insights-meta">
            {{ insightCount }} insight{{ insightCount === 1 ? "" : "s" }} ·
            {{ totalQuotes }} quote{{ totalQuotes === 1 ? "" : "s" }}
            <template v-if="chapters.length">
              ·
              {{ chapters.length }} chapter{{
                chapters.length === 1 ? "" : "s"
              }}
            </template>
          </div>

          <form
            v-if="addingChapter"
            class="alice-chat__composer"
            @submit.prevent="submitAddChapter"
          >
            <input
              ref="chapterInput"
              v-model="newChapterName"
              class="alice-chat__composer-input"
              type="text"
              placeholder="New chapter name (e.g. Onboarding)"
              maxlength="60"
              @keydown.esc.prevent="cancelAddChapter"
            />
            <button
              class="alice-chat__composer-create"
              type="submit"
              :disabled="!newChapterName.trim()"
            >
              Create
            </button>
            <button
              class="alice-chat__composer-cancel"
              type="button"
              title="Cancel"
              @click="cancelAddChapter"
            >
              ×
            </button>
          </form>

          <div class="alice-chat__insights-list">
            <InsightsContentsPanel
              :chapters="chapters"
              :readonly="$store.getters.isGuest"
              @open="openInsight"
              @remove-chapter="confirmRemoveChapter"
              @changed="onContentsChanged"
            >
              <template #empty>
                No chapters yet. Use “Add insight from chat” after the AI gives
                you something worth keeping, or add a chapter with +.
              </template>
            </InsightsContentsPanel>
          </div>

          <button
            class="alice-chat__add-from-chat"
            :disabled="!latestAssistantMessage"
            @click="addInsightFromLatest"
          >
            <Icon name="plus" :size="14" />
            <span>Add insight from chat</span>
          </button>
        </template>

        <template v-else>
          <div class="alice-chat__insights-meta">
            {{ savedNotes.length }} notitie{{
              savedNotes.length === 1 ? "" : "s"
            }}
          </div>
          <div class="alice-chat__saved-panel">
            <SavedNotesList
              :notes="savedNotes"
              @bring-to-chat="bringSavedToChat"
              @edit="editSavedNote"
              @delete="deleteSaved"
            >
              <template #empty>
                Nog niets bewaard. Selecteer tekst in een antwoord (of bewaar
                het hele antwoord) met de <strong>Bewaar</strong>-knop.
              </template>
            </SavedNotesList>
          </div>
          <button
            v-if="savedNotes.length"
            class="alice-chat__see-all-notes"
            type="button"
            @click="goToNotesPage"
          >
            <span>See all notes</span>
            <Icon name="arrow-right" :size="14" />
          </button>
        </template>
      </div>
    </aside>
  </div>
</template>

<script>
import { db, functions, storage } from "@/firebase";
import { httpsCallable } from "firebase/functions";
import {
  doc,
  updateDoc,
  addDoc,
  deleteDoc,
  collection,
  getDocs,
  query,
  where,
  setDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import { mapState } from "vuex";

import InsightDialog from "@/components/InsightDialog.vue";
import SaveNoteDialog from "@/components/SaveNoteDialog.vue";
import SavedNotesList from "@/components/SavedNotesList.vue";
import InsightsContentsPanel from "@/components/alice/InsightsContentsPanel.vue";
import ChatIndexRail from "@/components/alice/ChatIndexRail.vue";
import { buildConversationIndex } from "@/utils/conversationIndex";
import { sourceLabel, researchChipTitle } from "@/utils/researchSources";
import Icon from "@/components/alice/Icon.vue";
import Avatar from "@/components/alice/Avatar.vue";
import VP1Pattern from "@/components/alice/VP1Pattern.vue";
import ThinkingDots from "@/components/alice/ThinkingDots.vue";
import { colorForOwner } from "@/components/alice/normalize";
import communalChat from "@/mixins/communalChat";
import chatPresence from "@/mixins/chatPresence";
import voiceInput from "@/mixins/voiceInput";
import { newChapter, newInsight } from "@/utils/insightsModel";
import {
  replyAttention,
  beginReply,
  endReply,
  enableNotifications,
  disableNotifications,
} from "@/utils/replyAttention";

const MODEL_OPTIONS = [
  {
    title: "Claude Opus 5.5",
    value: "claude-opus-5-5",
    eco: "medium",
    ecoLabel: "Medium",
  },
  {
    title: "Claude Sonnet 5.5",
    value: "claude-sonnet-5-5",
    eco: "low",
    ecoLabel: "Low",
  },
  {
    title: "GPT-6 Sol",
    value: "gpt-6-sol",
    eco: "medium",
    ecoLabel: "Medium",
  },
  {
    title: "GPT-6 Luna",
    value: "gpt-6-luna",
    eco: "low",
    ecoLabel: "Low",
  },
  {
    title: "Gemini 3.1 Pro",
    value: "gemini-3.1-pro-preview",
    eco: "medium",
    ecoLabel: "Medium",
  },
  {
    title: "Gemini 3.8 Flash",
    value: "gemini-3.8-flash",
    eco: "low",
    ecoLabel: "Low",
  },
];

// Valsplat sustainability report — "Learn more" target in the eco note of
// the model dropdown.
const ECO_LEARN_MORE_URL =
  "https://drive.google.com/file/d/1OiGOUWz9Srbn5Ja6JX0zaPz7PriIPn1F/view?usp=drive_link";

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function renderMarkdown(input) {
  if (!input) return "";
  // Pre-existing payloads sometimes already contain <br /> from the old
  // codepath. Normalise to plain newlines first, then re-emit HTML.
  const raw = String(input).replace(/<br\s*\/?>/gi, "\n");
  const lines = raw.split("\n");
  const out = [];
  let inList = false;
  let inQuote = false;

  const closeList = () => {
    if (inList) {
      out.push("</ol>");
      inList = false;
    }
  };
  const closeQuote = () => {
    if (inQuote) {
      out.push("</blockquote>");
      inQuote = false;
    }
  };
  const inline = (s) => {
    let escaped = escapeHtml(s);
    escaped = escaped.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    escaped = escaped.replace(/\*([^*]+)\*/g, "<em>$1</em>");
    return escaped;
  };

  // A GFM table separator row, e.g. "|---|:--:|---:|" (dashes, optional
  // alignment colons, pipes and whitespace only — at least one dash).
  const isTableSeparator = (s) =>
    /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/.test(s) && s.includes("-");
  // Split a "| a | b | c |" row into trimmed cells, tolerating optional
  // leading/trailing pipes.
  const splitTableRow = (s) => {
    let t = s.trim();
    if (t.startsWith("|")) t = t.slice(1);
    if (t.endsWith("|")) t = t.slice(0, -1);
    return t.split("|").map((c) => c.trim());
  };
  const cellAlign = (c) => {
    const left = c.startsWith(":");
    const right = c.endsWith(":");
    if (left && right) return "center";
    if (right) return "right";
    if (left) return "left";
    return "";
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    const headingMatch = trimmed.match(/^(#{1,6})\s+(.+)/);
    const olMatch = trimmed.match(/^(\d+)\.\s+(.+)/);
    const quoteMatch = trimmed.match(/^>\s?(.*)/);
    const isTableHeader =
      trimmed.includes("|") &&
      i + 1 < lines.length &&
      isTableSeparator(lines[i + 1]);

    if (isTableHeader) {
      closeList();
      closeQuote();
      const headers = splitTableRow(trimmed);
      const aligns = splitTableRow(lines[i + 1]).map(cellAlign);
      const rows = [];
      let j = i + 2;
      while (
        j < lines.length &&
        lines[j].trim() !== "" &&
        lines[j].includes("|")
      ) {
        rows.push(splitTableRow(lines[j]));
        j++;
      }
      i = j - 1; // resume after the consumed table rows

      const alignAttr = (idx) =>
        aligns[idx] ? ` style="text-align:${aligns[idx]}"` : "";
      let table = '<div class="alice-chat__table-wrap"><table><thead><tr>';
      headers.forEach((h, idx) => {
        table += `<th${alignAttr(idx)}>${inline(h)}</th>`;
      });
      table += "</tr></thead><tbody>";
      rows.forEach((r) => {
        table += "<tr>";
        for (let c = 0; c < headers.length; c++) {
          table += `<td${alignAttr(c)}>${inline(r[c] || "")}</td>`;
        }
        table += "</tr>";
      });
      table += "</tbody></table></div>";
      out.push(table);
    } else if (headingMatch) {
      closeList();
      closeQuote();
      const level = headingMatch[1].length;
      out.push(`<h${level}>${inline(headingMatch[2])}</h${level}>`);
    } else if (olMatch) {
      closeQuote();
      if (!inList) {
        out.push("<ol>");
        inList = true;
      }
      out.push(`<li>${inline(olMatch[2])}</li>`);
    } else if (quoteMatch) {
      closeList();
      if (!inQuote) {
        out.push("<blockquote>");
        inQuote = true;
      }
      out.push(`<p>${inline(quoteMatch[1])}</p>`);
    } else if (trimmed === "") {
      closeList();
      closeQuote();
    } else {
      closeList();
      closeQuote();
      out.push(`<p>${inline(trimmed)}</p>`);
    }
  }
  closeList();
  closeQuote();
  return out.join("");
}

export default {
  name: "ProjectChat",
  components: {
    InsightDialog,
    SaveNoteDialog,
    SavedNotesList,
    InsightsContentsPanel,
    ChatIndexRail,
    Icon,
    Avatar,
    VP1Pattern,
    ThinkingDots,
  },
  mixins: [communalChat, chatPresence, voiceInput],
  data() {
    return {
      input: "",
      dialog: false,
      model: "claude-opus-5-5",
      modelOptions: MODEL_OPTIONS,
      ecoLearnMoreUrl: ECO_LEARN_MORE_URL,
      selectedChapter: null,
      selectedInsight: null,
      selectedInsightIndex: null,
      chatLoading: false,
      chatStartError: null,
      contextExpanded: false,
      // Per-user, per-project set of context items (files/recordings) the user
      // has toggled OFF. Stored by key (e.g. "recording:<id>"); anything not
      // listed is sent by default. Persisted to localStorage.
      disabledContextKeys: [],
      // Conversation index (bar rail): scroll-spy result, click suppression
      // window for the spy while a smooth scroll runs, rAF throttle handle.
      indexSpyKey: null,
      indexClickUntil: 0,
      indexScrollRaf: 0,
      // Chapter management
      addingChapter: false,
      newChapterName: "",
      // "Add insight from this answer" → inline new-chapter form (message
      // index the form is open for, or null)
      addingInsightChapterFor: null,
      insightChapterName: "",
      insightChapterInputEl: null,
      // Right-panel mode toggle: "insights" (Inhoud, default) or "saved" (Notes)
      insightPanel: "insights",
      // Saved-note dialog state
      saveDialog: false,
      saveDraft: null,
      editingSavedNote: null,
      // Floating "Save as note" button shown on text selection
      selectionButton: { show: false, x: 0, y: 0, text: "", message: null },
      // Per-message attachments (PDFs / images uploaded just for this turn)
      pendingAttachments: [],
      uploadingAttachments: false,
      dragDepth: 0,
      // Chat-local indicators (replace the app-wide setLoading overlay)
      awaitingReply: false,
      // Shared tab-title/favicon/notification state (see utils/replyAttention)
      replyAttention,
      formattingInsight: false,
      // Inline status banner for transient chat-level notices (e.g. when we
      // auto-switch the model after Claude rejects the prompt as too long).
      notice: "",
      insightsCollapsed: this.readInsightsCollapsed(),
      // Chat session versioning
      activeVersion: 1,
      viewingVersion: 1,
      sessions: [],
      compressing: false,
      // Active skills for the personal session (communal mode reads them off
      // the live communalMain doc instead). Pinned via the "/" picker or
      // auto-added when the model fetches one; session-sticky server-side.
      activeSkillIds: [],
      // "/" skill picker popover state. slashSuppressed: the user Esc'd out,
      // so stay closed until the leading "/" is gone (lets "/foo" send as-is).
      slashOpen: false,
      slashQuery: "",
      slashIndex: 0,
      slashSuppressed: false,
      // Transient "Alice activated skill X" banner — separate from `notice` so
      // it can't clobber the GPT-6 Sol fallback message.
      skillNotice: "",
      skillNoticeTimer: null,
      // Last rule the server learned from this session's reply ({id, text}),
      // shown as a chip with Undo (undo = dismiss, which is sticky).
      rulebookNotice: null,
      rulebookNoticeTimer: null,
      // Feedback the answer sent to a skill's owner (update_skill).
      skillFeedbackNotice: null,
      skillFeedbackNoticeTimer: null,
      // Dedupe for the communal lastAutoSkill notice.
      seenAutoSkillAt: 0,
      // Which answer's past-research sources list is expanded (message key).
      researchOpenFor: null,
    };
  },
  computed: {
    ...mapState({
      project: (state) => state.project,
      user: (state) => state.user,
    }),
    replyNotifyOn() {
      return (
        this.replyAttention.wanted &&
        this.replyAttention.permission === "granted"
      );
    },
    replyNotifyTitle() {
      if (this.replyAttention.permission === "denied")
        return "Notifications are blocked for Alice in your browser settings";
      return this.replyNotifyOn
        ? "Desktop notification when Alice is done: on"
        : "Notify me when Alice is done";
    },
    // Published skills, showing the frozen published version's metadata
    // (that's what the backend actually serves). Feeds the "/" picker and the
    // active-skill chips.
    publishedSkills() {
      return (this.$store.state.skills || [])
        .filter((s) => s.status === "published" && s.published)
        .map((s) => ({
          id: s.id,
          name: s.published.name,
          title: s.published.title || s.published.name,
          description: s.published.description || "",
        }));
    },
    // Active-skill IDs for the current mode: communal reads the live main doc
    // (kept fresh by its snapshot), personal uses local state synced via
    // chatStart / chatWithChat responses.
    currentActiveSkillIds() {
      return this.chatMode === "communal"
        ? (this.communalMain && this.communalMain.activeSkillIds) || []
        : this.activeSkillIds;
    },
    // Resolved chip objects; unknown IDs (skill unpublished since it was
    // pinned) simply render nothing — self-healing.
    activeSkills() {
      return this.currentActiveSkillIds
        .map((id) => this.publishedSkills.find((s) => s.id === id))
        .filter(Boolean);
    },
    slashResults() {
      const q = this.slashQuery.trim().toLowerCase();
      return this.publishedSkills
        .filter(
          (s) =>
            !q ||
            `${s.name} ${s.title} ${s.description}`.toLowerCase().includes(q)
        )
        .slice(0, 8);
    },
    hasMessages() {
      return this.visibleMessages.length > 0;
    },
    messages: {
      get() {
        return this.$store.state.chat;
      },
      set(newChat) {
        this.$store.commit("setChat", newChat);
      },
    },
    renderableMessages() {
      // The conversation starts with a system prompt and, only when the project
      // has attached files, a files-context message — both rendered separately
      // (the "files added to context" block). The filter below drops both: the
      // system message fails the role check, and the files-context message has
      // no display text. We must NOT blindly slice off the first two entries —
      // projects with no files (e.g. recordings-only) have a single preamble
      // message, so slice(2) used to swallow the first real user message.
      return (this.messages || []).filter((m) => {
        if (m.role !== "user" && m.role !== "assistant") return false;
        const text =
          typeof m.content === "string"
            ? m.content
            : typeof m.text === "string"
            ? m.text
            : "";
        if (!text.trim()) return false;
        return true;
      });
    },
    latestAssistantMessage() {
      const list = this.visibleMessages;
      for (let i = list.length - 1; i >= 0; i--) {
        if (list[i].role === "assistant") return list[i];
      }
      return null;
    },
    // Every file/recording that can be sent as context, each with a stable key
    // and its current on/off state. The disabled set is remembered per
    // user+project, so new files default to on.
    contextEntries() {
      const disabled = new Set(this.disabledContextKeys);
      const entries = [];
      if ((this.project?.obslogs || []).length) {
        const key = "obslog:log"; // stable sentinel — survives obslog re-creation
        entries.push({
          key,
          id: "log",
          name: "Observation log",
          type: "obslog",
          enabled: !disabled.has(key),
        });
      }
      if (this.allInsights.length) {
        const key = "insights:all"; // stable sentinel — one toggle for all
        entries.push({
          key,
          id: "all",
          name: `Insights (${this.allInsights.length})`,
          type: "insights",
          hint: "Saved insights only — unsaved edits on the Insights page are not sent.",
          enabled: !disabled.has(key),
        });
      }
      (this.project?.files || []).forEach((f) => {
        if (!f.name || !f.id) return;
        const key = `file:${f.id}`;
        entries.push({
          key,
          id: f.id,
          name: f.name,
          type: "file",
          enabled: !disabled.has(key),
        });
      });
      (this.project?.recordings || []).forEach((r) => {
        const id = r.uid || r.id;
        if (!r.name || !id) return;
        const key = `recording:${id}`;
        entries.push({
          key,
          id,
          name: r.name,
          type: "recording",
          tags: Array.isArray(r.tags) ? r.tags : [],
          enabled: !disabled.has(key),
        });
      });
      return entries;
    },
    enabledContextCount() {
      return this.contextEntries.filter((e) => e.enabled).length;
    },
    // Selection payload sent to the backend so it rebuilds context with only the
    // enabled files/recordings.
    contextSelection() {
      const entries = this.contextEntries;
      return {
        fileIds: entries
          .filter((e) => e.type === "file" && e.enabled)
          .map((e) => e.id),
        recordingIds: entries
          .filter((e) => e.type === "recording" && e.enabled)
          .map((e) => e.id),
        includeObslog:
          entries.some((e) => e.type === "obslog" && e.enabled) ||
          !entries.some((e) => e.type === "obslog"),
        includeInsights:
          entries.some((e) => e.type === "insights" && e.enabled) ||
          !entries.some((e) => e.type === "insights"),
      };
    },
    // One entry per exchange for the bar rail on the left.
    conversationIndex() {
      return buildConversationIndex(this.visibleMessages, {
        pendingReply: this.chatMode === "personal" && this.awaitingReply,
      });
    },
    indexEntries() {
      return this.conversationIndex.map((e) => ({
        ...e,
        time: this.formatTime(e.timestamp),
      }));
    },
    // Highlighted bar: the exchange in view (scroll-spy), else the latest.
    indexActiveKey() {
      const entries = this.conversationIndex;
      if (!entries.length) return null;
      if (this.indexSpyKey && entries.some((e) => e.key === this.indexSpyKey)) {
        return this.indexSpyKey;
      }
      return entries[entries.length - 1].key;
    },
    chapters() {
      return this.project?.chapters || [];
    },
    allInsights() {
      const out = [];
      this.chapters.forEach((c, ci) => {
        (c.insights || []).forEach((insight, ii) => {
          out.push({ insight, chapterIndex: ci, insightIndex: ii });
        });
      });
      return out;
    },
    insightCount() {
      return this.allInsights.length;
    },
    totalQuotes() {
      return this.allInsights.reduce(
        (sum, it) => sum + (it.insight.quotes?.length || 0),
        0
      );
    },
    canSend() {
      const hasText = !!String(this.input || "").trim();
      const hasAttachments = this.pendingAttachments.length > 0;
      if (this.chatMode === "communal") {
        // No awaiting/version gating — queueing while answering is allowed.
        return (
          !this.uploadingAttachments &&
          !this.lockedByOther &&
          (hasText || hasAttachments)
        );
      }
      return (
        !this.chatLoading &&
        !this.chatStartError &&
        !this.uploadingAttachments &&
        !this.awaitingReply &&
        this.viewingVersion === this.activeVersion &&
        (hasText || hasAttachments)
      );
    },
    userName() {
      return this.user?.displayName || this.user?.email || "";
    },
    userFirstName() {
      const name = this.userName;
      if (!name) return "you";
      return name.split(/[\s@]/)[0];
    },
    userPhoto() {
      return this.user?.photoURL || "";
    },
    userColor() {
      return colorForOwner(this.userName);
    },
    modelLabel() {
      const found = MODEL_OPTIONS.find((m) => m.value === this.model);
      return found ? found.title : this.model;
    },
    currentModel() {
      return MODEL_OPTIONS.find((m) => m.value === this.model) || null;
    },
    isViewingActiveSession() {
      if (this.chatMode === "communal") return this.communalIsViewingActive;
      return this.viewingVersion === this.activeVersion;
    },
    // Session picker sources, mode-aware. Personal state comes from chatStart
    // responses; communal state rides the live main-doc snapshot.
    sessionList() {
      return this.chatMode === "communal"
        ? this.communalSessions
        : this.sessions;
    },
    currentViewingVersion() {
      return this.chatMode === "communal"
        ? this.communalResolvedViewingVersion
        : this.viewingVersion;
    },
    currentActiveVersion() {
      return this.chatMode === "communal"
        ? this.communalActiveVersion
        : this.activeVersion;
    },
    savedNotes() {
      return this.project?.saved || [];
    },
    savedLabels() {
      const set = new Set();
      this.savedNotes.forEach((n) => {
        if (n.label) set.add(n.label);
      });
      return [...set].sort((a, b) => a.localeCompare(b));
    },
  },
  watch: {
    // Re-run the bar-rail scroll-spy when messages arrive or the session
    // switches, once the DOM has the new anchors.
    "visibleMessages.length"() {
      this.$nextTick(() => this.updateIndexActive());
    },
    "project.uid": "loadContextDisabled",
    "user.uid": "loadContextDisabled",
    // "/" skill picker: open while the draft starts with "/" (unless the user
    // Esc'd out). Runs alongside the communal mixin's own `input` watcher.
    input(val) {
      const v = String(val || "");
      if (!v.startsWith("/")) {
        this.slashSuppressed = false;
        this.closeSlash();
        return;
      }
      if (this.slashSuppressed || !this.publishedSkills.length) return;
      this.slashQuery = v.slice(1);
      this.slashIndex = 0;
      this.slashOpen = true;
    },
    // Communal: the drain loop stamps lastAutoSkill when the model fetched a
    // skill — surface it as a transient notice (teammate pins don't stamp it).
    communalMain(main) {
      const auto = main && main.lastAutoSkill;
      if (
        auto &&
        auto.at &&
        auto.at !== this.seenAutoSkillAt &&
        Date.now() - auto.at < 20000
      ) {
        this.seenAutoSkillAt = auto.at;
        this.showSkillNotice(auto.names || []);
      }
    },
  },
  async mounted() {
    this.loadContextDisabled();
    await this.loadSession();
    // A saved note may have handed us text via "Terug in chat" from another
    // route. Consume it once and clear so it doesn't reappear on next mount.
    const prefill = this.$store.state.chatPrefill;
    if (prefill) {
      this.input = prefill;
      this.$store.commit("setChatPrefill", null);
      this.$nextTick(() => {
        if (this.$refs.textareaRef) {
          this.$refs.textareaRef.focus();
          this.autoSizeTextarea({ target: this.$refs.textareaRef });
        }
      });
    }
  },
  beforeUnmount() {
    clearTimeout(this.skillNoticeTimer);
    clearTimeout(this.skillFeedbackNoticeTimer);
    if (this.indexScrollRaf) cancelAnimationFrame(this.indexScrollRaf);
  },
  methods: {
    renderMarkdown,
    async toggleReplyNotifications() {
      if (this.replyNotifyOn) return disableNotifications();
      if (await enableNotifications()) {
        this.notice =
          "Notifications are on. No test popup? Allow your browser under macOS System Settings → Notifications.";
        return;
      }
      this.notice =
        this.replyAttention.permission === "denied"
          ? "Notifications are blocked for Alice. Allow them via the icon left of the address bar, then click the bell again."
          : "Notifications weren't turned on. The browser tab still shows when Alice is done.";
    },
    // Tab/notification text for a settled reply. Deliberately no preview of
    // the answer: notifications also pop up while screen-sharing.
    replyNotice(ok) {
      const projectUid = this.project?.uid || this.$route.params.id;
      const where = this.project?.title ? ` in ${this.project.title}` : "";
      const chatPath = `/project/${projectUid}/chat`;
      const router = this.$router;
      return {
        title: ok ? "Alice is done" : "Alice couldn't answer",
        failed: !ok,
        body: ok
          ? `Your answer${where} is ready.`
          : `Something went wrong${where}. Open the chat to try again.`,
        onClick: () => {
          if (router.currentRoute.value.path !== chatPath)
            router.push(chatPath);
        },
      };
    },
    formatTime(ts) {
      if (!ts) return "";
      const d = new Date(ts);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    autoSizeTextarea(e) {
      const el = e?.target || this.$refs.textareaRef;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    },
    handleKeydown(event) {
      // While the "/" skill picker is open its keys take priority; plain
      // Enter-to-send resumes once it's closed.
      if (this.slashOpen) {
        if (event.key === "ArrowDown") {
          event.preventDefault();
          this.slashIndex = Math.min(
            this.slashIndex + 1,
            this.slashResults.length - 1
          );
          this.scrollSlashIntoView();
          return;
        }
        if (event.key === "ArrowUp") {
          event.preventDefault();
          this.slashIndex = Math.max(this.slashIndex - 1, 0);
          this.scrollSlashIntoView();
          return;
        }
        if (event.key === "Escape") {
          event.preventDefault();
          this.slashSuppressed = true;
          this.closeSlash();
          return;
        }
        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault();
          if (this.slashResults.length) {
            this.selectSlashSkill(this.slashResults[this.slashIndex]);
          } else {
            // No matching skill — treat the "/..." draft as a literal message.
            this.closeSlash();
            this.send();
          }
          return;
        }
      }
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        this.send();
      }
    },
    closeSlash() {
      this.slashOpen = false;
      this.slashIndex = 0;
    },
    scrollSlashIntoView() {
      this.$nextTick(() => {
        const list = this.$refs.slashListRef;
        const active = list && list.querySelector(".is-active");
        if (active) active.scrollIntoView({ block: "nearest" });
      });
    },
    selectSlashSkill(skill) {
      if (!skill) return;
      this.pinSkill(skill.id);
      this.input = "";
      this.closeSlash();
      this.$nextTick(() => {
        if (this.$refs.textareaRef) {
          this.$refs.textareaRef.focus();
          this.autoSizeTextarea({ target: this.$refs.textareaRef });
        }
      });
    },
    // Pin/unpin a skill for the current session. The server re-validates the
    // set against published skills on every send, so these writes only steer —
    // they can't inject anything unpublished.
    async pinSkill(id) {
      if (this.currentActiveSkillIds.includes(id)) return;
      try {
        if (this.chatMode === "communal") {
          await setDoc(
            this.communalMainRef(),
            { activeSkillIds: arrayUnion(id), updatedAt: Date.now() },
            { merge: true }
          );
          return; // the main-doc snapshot updates the chips
        }
        this.activeSkillIds = [...this.activeSkillIds, id]; // optimistic
        await setDoc(
          doc(db, "projects", this.project.uid, "chats", this.user.uid),
          { activeSkillIds: arrayUnion(id) },
          { merge: true }
        );
      } catch (err) {
        console.error("pinSkill failed:", err);
      }
    },
    async unpinSkill(id) {
      try {
        if (this.chatMode === "communal") {
          await setDoc(
            this.communalMainRef(),
            { activeSkillIds: arrayRemove(id), updatedAt: Date.now() },
            { merge: true }
          );
          return;
        }
        this.activeSkillIds = this.activeSkillIds.filter((s) => s !== id);
        await setDoc(
          doc(db, "projects", this.project.uid, "chats", this.user.uid),
          { activeSkillIds: arrayRemove(id) },
          { merge: true }
        );
      } catch (err) {
        console.error("unpinSkill failed:", err);
      }
    },
    // Past-research provenance (researchMeta) on an assistant message: the
    // sources chip toggles a list of the projects Alice consulted.
    researchSourceCount(m) {
      const meta = m && m.researchMeta;
      return meta && Array.isArray(meta.sources) ? meta.sources.length : 0;
    },
    researchKey(m, i) {
      return (m && (m.uid || m.id)) || `i:${i}`;
    },
    toggleResearch(m, i) {
      const key = this.researchKey(m, i);
      this.researchOpenFor = this.researchOpenFor === key ? null : key;
    },
    sourceLabel,
    researchChipTitle,
    // Fold a chatWithChat reply's skill info into local state: MERGE the
    // newly-loaded IDs (don't replace with the server's view — it predates any
    // pin/unpin made while the reply was in flight).
    applySkillReply(reply) {
      const loaded = Array.isArray(reply && reply.loadedSkillIds)
        ? reply.loadedSkillIds
        : [];
      if (!loaded.length) return;
      this.activeSkillIds = [...new Set([...this.activeSkillIds, ...loaded])];
      this.showSkillNotice(
        loaded.map(
          (id) => (this.publishedSkills.find((s) => s.id === id) || {}).title
        )
      );
    },
    // A chatWithChat reply may carry `rulebookLearned`: rules the learn pass
    // just added to this researcher's rulebook. Surface the first one with an
    // Undo, and refresh the store copy so the profile page is current.
    applyRulebookReply(reply) {
      const learned = Array.isArray(reply && reply.rulebookLearned)
        ? reply.rulebookLearned
        : [];
      if (!learned.length) return;
      this.rulebookNotice = learned[0];
      clearTimeout(this.rulebookNoticeTimer);
      this.rulebookNoticeTimer = setTimeout(() => {
        this.rulebookNotice = null;
      }, 12000);
      this.$store.dispatch("fetchRulebook");
    },
    // A chatWithChat reply may carry `skillFeedback`: what the model sent to a
    // skill's owner through update_skill this turn.
    applySkillFeedbackReply(reply) {
      const sent = Array.isArray(reply && reply.skillFeedback)
        ? reply.skillFeedback
        : [];
      if (!sent.length) return;
      this.skillFeedbackNotice = sent[0];
      clearTimeout(this.skillFeedbackNoticeTimer);
      this.skillFeedbackNoticeTimer = setTimeout(() => {
        this.skillFeedbackNotice = null;
      }, 10000);
    },
    undoRulebookRule() {
      const rule = this.rulebookNotice;
      this.rulebookNotice = null;
      clearTimeout(this.rulebookNoticeTimer);
      if (rule && rule.id) this.$store.dispatch("dismissRule", rule.id);
    },
    showSkillNotice(names) {
      const list = (names || []).filter(Boolean);
      if (!list.length) return;
      this.skillNotice = `Alice activated skill ${list
        .map((n) => `“${n}”`)
        .join(", ")} — active for this session until you remove it.`;
      clearTimeout(this.skillNoticeTimer);
      this.skillNoticeTimer = setTimeout(() => {
        this.skillNotice = "";
      }, 8000);
    },
    scrollToBottom() {
      const el = this.$refs.bodyRef;
      if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    },
    // Long answers: land on the start of the newest AI message instead of
    // its end. Rect-based so it doesn't depend on which ancestor is the
    // offsetParent; the browser clamps short answers to max-scroll anyway.
    scrollToAnswerStart() {
      const el = this.$refs.bodyRef;
      if (!el) return;
      const articles = el.querySelectorAll(".alice-chat__msg--ai");
      const last = articles[articles.length - 1];
      if (!last) {
        this.scrollToBottom();
        return;
      }
      const top =
        last.getBoundingClientRect().top -
        el.getBoundingClientRect().top +
        el.scrollTop -
        12;
      el.scrollTo({ top, behavior: "smooth" });
    },
    scrollToMessageIndex(index) {
      const el = this.$refs.bodyRef;
      if (!el) return;
      const target = el.querySelector(`[data-msg-index="${index}"]`);
      if (!target) return;
      const top =
        target.getBoundingClientRect().top -
        el.getBoundingClientRect().top +
        el.scrollTop -
        12;
      el.scrollTo({ top, behavior: "smooth" });
    },
    // ----- Conversation index (bar rail) -----
    onBodyScroll() {
      this.hideSelectionButton();
      if (this.indexScrollRaf) return;
      this.indexScrollRaf = requestAnimationFrame(() => {
        this.indexScrollRaf = 0;
        this.updateIndexActive();
      });
    },
    // Active = the last exchange whose question sits above ~35% of the
    // viewport; at the very bottom, the latest one.
    updateIndexActive() {
      if (Date.now() < this.indexClickUntil) return;
      const body = this.$refs.bodyRef;
      if (!body) return;
      const entries = this.conversationIndex;
      if (!entries.length) {
        this.indexSpyKey = null;
        return;
      }
      const atBottom =
        body.scrollTop + body.clientHeight >= body.scrollHeight - 8;
      if (atBottom) {
        this.indexSpyKey = entries[entries.length - 1].key;
        return;
      }
      const bodyTop = body.getBoundingClientRect().top;
      const threshold = Math.max(120, body.clientHeight * 0.35);
      let active = entries[0].key;
      for (const e of entries) {
        const el = body.querySelector(`[data-msg-index="${e.userIndex}"]`);
        if (!el) continue;
        const rel = el.getBoundingClientRect().top - bodyTop;
        if (rel <= threshold) active = e.key;
        else break;
      }
      this.indexSpyKey = active;
    },
    onIndexSelect(entry) {
      this.indexSpyKey = entry.key;
      // Keep the spy from fighting the smooth scroll.
      this.indexClickUntil = Date.now() + 700;
      this.scrollToMessageIndex(entry.userIndex);
    },

    closeDialog() {
      this.dialog = false;
      this.selectedInsight = null;
    },
    openInsight(it) {
      this.selectedInsight = it.insight;
      this.selectedChapter = it.chapterIndex;
      this.selectedInsightIndex = it.insightIndex;
      this.dialog = true;
    },
    // Drag & drop in the Inhoud panel mutated the tree in place; persist it
    // (incremental diff) like the other edits made from the chat aside.
    onContentsChanged() {
      this.$store.dispatch("updateProject");
    },

    addChapter(title = "") {
      if (!this.project) return -1;
      if (!this.project.chapters) this.project.chapters = [];
      this.project.chapters.push(newChapter({ title }));
      return this.project.chapters.length - 1;
    },
    toggleAddChapter() {
      if (this.addingChapter) {
        this.cancelAddChapter();
      } else {
        this.startAddChapter();
      }
    },
    startAddChapter() {
      this.addingChapter = true;
      this.newChapterName = "";
      this.$nextTick(() => {
        if (this.$refs.chapterInput) this.$refs.chapterInput.focus();
      });
    },
    cancelAddChapter() {
      this.addingChapter = false;
      this.newChapterName = "";
    },
    submitAddChapter() {
      const title = this.newChapterName.trim();
      if (!title) return;
      const idx = this.addChapter(title);
      this.addingChapter = false;
      this.newChapterName = "";
      if (idx >= 0) this.$store.dispatch("updateProject");
    },
    setInsightChapterInput(el) {
      // Function ref: string refs inside v-for accumulate stale array
      // entries across open/close cycles, breaking refocus.
      this.insightChapterInputEl = el || null;
    },
    startInsightChapter(messageIndex) {
      this.addingInsightChapterFor = messageIndex;
      this.insightChapterName = "";
      this.$nextTick(() => {
        if (this.insightChapterInputEl) this.insightChapterInputEl.focus();
      });
    },
    cancelInsightChapter() {
      this.addingInsightChapterFor = null;
      this.insightChapterName = "";
    },
    submitInsightChapter(m) {
      const title = this.insightChapterName.trim();
      if (!title) return;
      const idx = this.addChapter(title);
      this.cancelInsightChapter();
      if (idx < 0) return;
      this.addInsight(idx, m.content);
    },
    confirmRemoveChapter(index) {
      const chapter = this.chapters[index];
      if (!chapter) return;
      const label = chapter.title || "Untitled";
      const insightCount = (chapter.insights || []).length;
      const message = insightCount
        ? `Delete chapter "${label}" and its ${insightCount} insight${
            insightCount === 1 ? "" : "s"
          }?`
        : `Delete chapter "${label}"?`;
      if (!confirm(message)) return;
      this.project.chapters.splice(index, 1);
      this.$store.dispatch("updateProject");
    },
    async addInsight(chapterIndex, content) {
      this.formattingInsight = true;
      try {
        const formatInsight = httpsCallable(functions, "formatInsight");
        const res = await formatInsight({ insight: content });
        const formatted = newInsight({
          title: res.data.title || "Title",
          topic: res.data.topic || "",
          findings: res.data.findings
            ? res.data.findings.map((f) => ({ text: f }))
            : [],
          quotes: res.data.quotes
            ? res.data.quotes.map((q) => ({
                text: q.quote,
                selected: false,
                name: q.name,
              }))
            : [],
          strength: res.data.strength || null,
        });
        this.project.chapters[chapterIndex].insights.push(formatted);
        this.$store.dispatch("updateProject");
      } catch (err) {
        console.error("formatInsight failed:", err);
      } finally {
        this.formattingInsight = false;
      }
    },
    addInsightFromLatest() {
      const m = this.latestAssistantMessage;
      if (!m) return;
      // Last chapter (where new material usually belongs); create one if
      // none exist.
      let target = (this.project.chapters || []).length - 1;
      if (target < 0) target = this.addChapter();
      this.addInsight(target, m.content);
    },
    removeInsight() {
      if (!this.selectedInsight) return;
      // Match by identity/uid, not title — two insights may share a title.
      const target = this.selectedInsight;
      this.project.chapters.forEach((c) => {
        const idx = (c.insights || []).findIndex(
          (ins) => ins === target || (target.uid && ins.uid === target.uid)
        );
        if (idx >= 0) c.insights.splice(idx, 1);
      });
      this.closeDialog();
      this.$store.dispatch("updateProject");
    },
    updateInsight(draft) {
      // The dialog edits a copy; apply it onto the live insight (still held by
      // reference inside project.chapters) so the saved project reflects it.
      if (draft && this.selectedInsight) {
        Object.assign(this.selectedInsight, draft);
      }
      this.dialog = false;
      this.$store.dispatch("updateProject");
    },

    // ----- Saved notes (lightweight bookmarks) -----
    openSaveDialog(text, message) {
      this.editingSavedNote = null;
      this.saveDraft = {
        text,
        label: "",
        note: null,
        source: message
          ? {
              messageUid: message.uid || null,
              role: "assistant",
              timestamp: message.timestamp || null,
              version: message.version ?? this.currentViewingVersion,
            }
          : null,
      };
      this.saveDialog = true;
    },
    startSaveFromMessage(m, event) {
      let text = "";
      // Prefer the user's current text selection if it sits inside this answer.
      const sel = window.getSelection ? window.getSelection() : null;
      if (sel && !sel.isCollapsed && sel.rangeCount) {
        const range = sel.getRangeAt(0);
        const article = event?.currentTarget?.closest(".alice-chat__msg--ai");
        const aiEl = article ? article.querySelector(".alice-chat__ai") : null;
        if (aiEl && aiEl.contains(range.commonAncestorContainer)) {
          text = sel.toString().trim();
        }
      }
      // Otherwise fall back to the whole answer.
      if (!text) text = this.messageText(m);
      this.openSaveDialog(text, m);
    },
    // Show a floating "Save as note" button when the user selects text inside
    // an AI answer.
    handleTextSelection() {
      const sel = window.getSelection ? window.getSelection() : null;
      if (!sel || sel.isCollapsed || !sel.rangeCount) {
        this.hideSelectionButton();
        return;
      }
      const range = sel.getRangeAt(0);
      let node = range.commonAncestorContainer;
      if (node && node.nodeType === 3) node = node.parentElement;
      const aiEl =
        node && node.closest ? node.closest(".alice-chat__ai") : null;
      const text = sel.toString().trim();
      if (!aiEl || !text) {
        this.hideSelectionButton();
        return;
      }
      const article = aiEl.closest(".alice-chat__msg--ai");
      const idx = article ? Number(article.dataset.msgIndex) : -1;
      // data-msg-index is stamped from `visibleMessages` in the template (the
      // mode-aware list: personal or communal), so read it back from that same
      // list — not from `renderableMessages`, which is personal-mode only.
      const message = this.visibleMessages[idx] || null;
      const rect = range.getBoundingClientRect();
      this.selectionButton = {
        show: true,
        x: rect.left + rect.width / 2,
        y: rect.top - 8,
        text,
        message,
      };
    },
    hideSelectionButton() {
      if (this.selectionButton.show) {
        this.selectionButton = {
          show: false,
          x: 0,
          y: 0,
          text: "",
          message: null,
        };
      }
    },
    saveSelectionAsNote() {
      const { text, message } = this.selectionButton;
      if (text) this.openSaveDialog(text, message);
      this.hideSelectionButton();
      const sel = window.getSelection ? window.getSelection() : null;
      if (sel) sel.removeAllRanges();
    },
    closeSaveDialog() {
      this.saveDialog = false;
      this.saveDraft = null;
      this.editingSavedNote = null;
    },
    async onSaveNote(payload) {
      if (payload.uid) {
        await this.$store.dispatch("updateSavedNote", payload);
      } else {
        await this.$store.dispatch("addSavedNote", payload);
      }
      this.closeSaveDialog();
    },
    editSavedNote(note) {
      this.saveDraft = null;
      this.editingSavedNote = note;
      this.saveDialog = true;
    },
    deleteSaved(note) {
      this.$store.dispatch("deleteSavedNote", note.uid);
    },
    bringSavedToChat(note) {
      this.input = note.text || "";
      this.$nextTick(() => {
        if (this.$refs.textareaRef) {
          this.$refs.textareaRef.focus();
          this.autoSizeTextarea({ target: this.$refs.textareaRef });
        }
      });
    },
    goToNotesPage() {
      const id = this.project?.uid || this.$route.params.id;
      this.$router.push(`/project/${id}/saved`);
    },

    readInsightsCollapsed() {
      try {
        return localStorage.getItem("valsplat.insightsCollapsed") === "1";
      } catch {
        return false;
      }
    },
    toggleInsights() {
      this.insightsCollapsed = !this.insightsCollapsed;
      try {
        localStorage.setItem(
          "valsplat.insightsCollapsed",
          this.insightsCollapsed ? "1" : "0"
        );
      } catch {
        // localStorage unavailable — non-fatal
      }
    },

    // ----- Context file/recording toggles (which files are sent per message) -
    contextStorageKey() {
      const projectUid = this.project?.uid || this.$route.params.id || "";
      const userUid = this.user?.uid || "";
      return `valsplat.contextDisabled.${projectUid}.${userUid}`;
    },
    loadContextDisabled() {
      try {
        const raw = localStorage.getItem(this.contextStorageKey());
        const parsed = raw ? JSON.parse(raw) : [];
        this.disabledContextKeys = Array.isArray(parsed) ? parsed : [];
      } catch {
        this.disabledContextKeys = [];
      }
    },
    persistContextDisabled() {
      try {
        localStorage.setItem(
          this.contextStorageKey(),
          JSON.stringify(this.disabledContextKeys)
        );
      } catch {
        // localStorage unavailable — non-fatal
      }
    },
    toggleContextItem(entry) {
      const set = new Set(this.disabledContextKeys);
      if (set.has(entry.key)) {
        set.delete(entry.key);
      } else {
        set.add(entry.key);
      }
      this.disabledContextKeys = [...set];
      this.persistContextDisabled();
    },
    setAllContext(enabled) {
      this.disabledContextKeys = enabled
        ? []
        : this.contextEntries.map((e) => e.key);
      this.persistContextDisabled();
    },

    async loadSession(version) {
      this.chatLoading = true;
      this.chatStartError = null;
      try {
        const chatStart = httpsCallable(functions, "chatStart");
        const payload = { project: this.$route.params.id };
        if (typeof version === "number") payload.version = version;
        const res = await chatStart(payload);
        // Pre-versioning backend returned `messages` directly as an array.
        // Versioning backend returns `{ messages, activeVersion, viewingVersion, sessions }`.
        // Tolerate both so frontend + backend can deploy independently.
        const raw = res.data;
        if (Array.isArray(raw)) {
          this.messages = raw;
          this.activeVersion = 1;
          this.viewingVersion = 1;
          this.sessions = [];
          this.activeSkillIds = [];
        } else {
          const data = raw || {};
          this.messages = data.messages || [];
          if (typeof data.activeVersion === "number") {
            this.activeVersion = data.activeVersion;
          }
          if (typeof data.viewingVersion === "number") {
            this.viewingVersion = data.viewingVersion;
          }
          if (Array.isArray(data.sessions)) {
            this.sessions = data.sessions;
          }
          this.activeSkillIds = Array.isArray(data.activeSkillIds)
            ? data.activeSkillIds
            : [];
        }
        this.$nextTick(() => this.scrollToBottom());
      } catch (err) {
        console.error("chatStart failed:", err);
        this.chatStartError =
          err?.message || "Could not load chat. Please refresh and try again.";
      } finally {
        this.chatLoading = false;
      }
    },
    async onSessionChange(version) {
      const v = Number(version);
      if (!Number.isFinite(v)) return;
      if (this.chatMode === "communal") {
        // No fetch needed: every session already sits in the snapshot and
        // communalRenderable filters on the viewed version.
        this.communalViewingVersion =
          v === this.communalActiveVersion ? null : v;
        this.$nextTick(() => this.scrollToBottom());
        return;
      }
      if (v === this.viewingVersion) return;
      await this.loadSession(v);
    },
    async compressActiveChat() {
      if (this.compressing) return;
      if (this.viewingVersion !== this.activeVersion) return;
      const ok = confirm(
        "Compress this chat? The current session will be archived and a new session will start, seeded with an AI summary."
      );
      if (!ok) return;
      this.compressing = true;
      this.notice = "";
      try {
        const compressChat = httpsCallable(functions, "compressChat", {
          timeout: 540000,
        });
        const res = await compressChat({
          project: this.project.uid,
          model: this.model,
        });
        const data = res.data || {};
        if (Array.isArray(data.sessions)) this.sessions = data.sessions;
        if (typeof data.activeVersion === "number") {
          this.activeVersion = data.activeVersion;
        }
        await this.loadSession(data.activeVersion);
        this.notice = "Chat compressed. New session started with summary.";
      } catch (err) {
        console.error("compressChat failed:", err);
        this.notice =
          err?.message || "Could not compress the chat. Please try again.";
      } finally {
        this.compressing = false;
      }
    },

    async clearChat() {
      if (this.viewingVersion !== this.activeVersion) return;
      const r = confirm("Are you sure? This will delete all current messages.");
      if (!r) return;

      // Keep only the non-conversational preamble: the system prompt and, when
      // the project has files, the files-context message. Identify them
      // structurally rather than by index — projects without files have no
      // files-context message, so the old messages[0]/[1] assumption kept the
      // first real user message by mistake. (A user's own attachment message
      // also has array content, but always carries `attachments`, so the
      // `!m.attachments` check excludes it.)
      this.messages = (this.messages || []).filter(
        (m) =>
          m.role === "system" ||
          (m.role === "user" && Array.isArray(m.content) && !m.attachments)
      );

      const querySnapshot = await getDocs(
        query(
          collection(
            db,
            "projects",
            this.project.uid,
            "chats",
            this.user.uid,
            "messages"
          ),
          where("version", "==", this.viewingVersion)
        )
      );
      await Promise.all(
        querySnapshot.docs.map((d) =>
          deleteDoc(
            doc(
              db,
              "projects",
              this.project.uid,
              "chats",
              this.user.uid,
              "messages",
              d.id
            )
          )
        )
      );
    },

    messageText(m) {
      if (typeof m.content === "string") return m.content;
      if (typeof m.text === "string") return m.text;
      return "";
    },

    openFilePicker() {
      if (this.chatLoading || this.chatStartError) return;
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },

    addAttachmentFiles(files, { synthesizeNames = false } = {}) {
      const allowed = [
        "application/pdf",
        "image/png",
        "image/jpeg",
        "image/webp",
        "image/gif",
      ];
      const accepted = files.filter((f) => allowed.includes(f.type));
      const rejected = files.filter((f) => !allowed.includes(f.type));
      if (rejected.length) {
        const names = rejected.map((f) => f.name || f.type).join(", ");
        alert(
          `${names}: only PDF, PNG, JPEG, WebP and GIF files are supported.`
        );
      }
      const stamp = Date.now();
      accepted.forEach((file, i) => {
        const isImage = file.type.startsWith("image/");
        let name = file.name;
        if ((synthesizeNames && isImage) || !name) {
          const ext = (file.type.split("/")[1] || "bin").replace("jpeg", "jpg");
          const suffix = accepted.length > 1 ? `-${i + 1}` : "";
          name = `${isImage ? "screenshot" : "file"}-${stamp}${suffix}.${ext}`;
        }
        this.pendingAttachments.push({
          file,
          name,
          contentType: file.type,
          progress: 0,
          previewUrl: isImage ? URL.createObjectURL(file) : null,
        });
      });
    },

    onFilePick(event) {
      const files = Array.from(event.target?.files || []);
      this.addAttachmentFiles(files);
      // Reset so the same file can be picked again later
      if (event.target) event.target.value = "";
    },

    handlePaste(event) {
      const clip = event.clipboardData;
      if (!clip) return;
      // Clipboard carries text (e.g. copied spreadsheet cells): paste as text
      if (clip.getData("text/plain").trim()) return;
      const files = Array.from(clip.items || [])
        .filter((item) => item.kind === "file")
        .map((item) => item.getAsFile())
        .filter(Boolean);
      if (!files.length) return;
      event.preventDefault();
      this.addAttachmentFiles(files, { synthesizeNames: true });
    },

    dragHasFiles(event) {
      return Array.from(event.dataTransfer?.types || []).includes("Files");
    },

    onDragEnter(event) {
      if (!this.dragHasFiles(event) || this.inputDisabled) return;
      event.preventDefault();
      this.dragDepth += 1;
    },

    onDragOver(event) {
      if (!this.dragHasFiles(event) || this.inputDisabled) return;
      event.preventDefault();
    },

    onDragLeave(event) {
      if (!this.dragHasFiles(event) || this.dragDepth === 0) return;
      this.dragDepth -= 1;
    },

    onDrop(event) {
      this.dragDepth = 0;
      if (!this.dragHasFiles(event) || this.inputDisabled) return;
      event.preventDefault();
      const files = Array.from(event.dataTransfer?.files || []);
      if (files.length) this.addAttachmentFiles(files);
    },

    removePending(index) {
      const att = this.pendingAttachments[index];
      if (att?.previewUrl) URL.revokeObjectURL(att.previewUrl);
      this.pendingAttachments.splice(index, 1);
    },

    async uploadPendingAttachments() {
      const uploaded = [];
      for (const att of this.pendingAttachments) {
        const safeName = att.name.replace(/[^\w.-]+/g, "_");
        const path =
          this.chatMode === "communal"
            ? `projects/${this.project.uid}/communalChat/temp/${
                this.user.uid
              }_${Date.now()}_${safeName}`
            : `projects/${this.project.uid}/chats/${
                this.user.uid
              }/temp/${Date.now()}_${safeName}`;
        const ref = storageRef(storage, path);
        att.progress = 0;
        const task = uploadBytesResumable(ref, att.file, {
          contentType: att.contentType,
        });
        await new Promise((resolve, reject) => {
          task.on(
            "state_changed",
            (snap) => {
              att.progress = snap.totalBytes
                ? snap.bytesTransferred / snap.totalBytes
                : 0;
            },
            reject,
            resolve
          );
        });
        att.progress = 1;
        const url = await getDownloadURL(ref);
        if (att.previewUrl) {
          URL.revokeObjectURL(att.previewUrl);
          att.previewUrl = null;
        }
        uploaded.push({
          name: att.name,
          contentType: att.contentType,
          url,
        });
      }
      return uploaded;
    },

    async send() {
      if (this.chatMode === "communal") return this.sendCommunal();
      if (!this.canSend) return;
      this.notice = "";

      const text = String(this.input || "").trim();
      const hasAttachments = this.pendingAttachments.length > 0;

      let attachments = [];
      if (hasAttachments) {
        this.uploadingAttachments = true;
        try {
          attachments = await this.uploadPendingAttachments();
        } catch (error) {
          console.error("attachment upload failed:", error);
          alert("Could not upload attachments. Please try again.");
          this.uploadingAttachments = false;
          return;
        }
        this.uploadingAttachments = false;
      }

      // Build content: plain string when no attachments (preserves the
      // existing OpenAI/Claude behavior); structured array when there are
      // attachments — both providers handle these block shapes.
      let content;
      if (attachments.length) {
        content = [];
        if (text) content.push({ type: "input_text", text });
        for (const a of attachments) {
          if (a.contentType.startsWith("image/")) {
            content.push({ type: "input_image", image_url: a.url });
          } else {
            content.push({ type: "input_file", file_url: a.url });
          }
        }
      } else {
        content = text;
      }

      const userMsg = {
        role: "user",
        content,
        timestamp: Date.now(),
        version: this.viewingVersion,
      };
      if (attachments.length) {
        userMsg.text = text;
        userMsg.attachments = attachments;
      }

      this.messages.push(userMsg);
      this.input = "";
      this.pendingAttachments = [];
      this.awaitingReply = true;
      const replyKey = `personal:${this.project.uid}:${userMsg.timestamp}`;
      let replied = false;
      beginReply(replyKey);
      this.$nextTick(() => {
        this.scrollToBottom();
        if (this.$refs.textareaRef)
          this.$refs.textareaRef.style.height = "auto";
      });

      try {
        const docRef = await addDoc(
          collection(
            db,
            "projects",
            this.project.uid,
            "chats",
            this.user.uid,
            "messages"
          ),
          userMsg
        );
        await updateDoc(
          doc(
            db,
            "projects",
            this.project.uid,
            "chats",
            this.user.uid,
            "messages",
            docRef.id
          ),
          { uid: docRef.id }
        );

        const chatWithChat = httpsCallable(functions, "chatWithChat", {
          timeout: 540000,
        });
        const res = await chatWithChat({
          messages: this.messages,
          model: this.model,
          project: this.project,
          version: this.viewingVersion,
          contextSelection: this.contextSelection,
        });
        const reply = res.data || {};
        if (!reply.timestamp) reply.timestamp = Date.now();
        this.messages.push(reply);
        replied = true;
        this.applySkillReply(reply);
        this.applyRulebookReply(reply);
        this.applySkillFeedbackReply(reply);
        this.$nextTick(() => this.scrollToAnswerStart());
      } catch (error) {
        console.error("chatWithChat failed:", error);
        const code = error?.code || "";
        const message = error?.message || "";
        // Guest token budget spent — same error code as context-too-long, so
        // this must be checked BEFORE the model-switch retry below (a retry
        // would burn nothing but can never succeed).
        if (/GUEST_BUDGET_EXHAUSTED/.test(message)) {
          this.notice =
            "Your AI budget for this project has been used up. Ask your Valsplat contact to raise it.";
          return;
        }
        const isContextTooLong =
          code === "functions/resource-exhausted" ||
          code === "resource-exhausted" ||
          /CLAUDE_CONTEXT_TOO_LONG|prompt is too long/i.test(message);
        if (isContextTooLong && this.model !== "gpt-6-sol") {
          const overModel = this.modelLabel;
          this.model = "gpt-6-sol";
          this.notice = `${overModel} is over capacity for this conversation. Switching to GPT-6 Sol…`;
          try {
            const chatWithChat = httpsCallable(functions, "chatWithChat", {
              timeout: 540000,
            });
            const res = await chatWithChat({
              messages: this.messages,
              model: this.model,
              project: this.project,
              version: this.viewingVersion,
              contextSelection: this.contextSelection,
            });
            const reply = res.data || {};
            if (!reply.timestamp) reply.timestamp = Date.now();
            this.messages.push(reply);
            replied = true;
            this.applySkillReply(reply);
            this.applyRulebookReply(reply);
            this.applySkillFeedbackReply(reply);
            this.notice = `Switched to GPT-6 Sol — ${overModel} was over capacity for this conversation.`;
            this.$nextTick(() => this.scrollToAnswerStart());
          } catch (retryError) {
            console.error("retry on gpt-6-sol failed:", retryError);
            this.notice =
              "Switched to GPT-6 Sol but the retry also failed. Clear the chat or detach some files and try again.";
          }
        } else {
          this.notice = "Alice couldn't answer this time. Please try again.";
        }
      } finally {
        this.awaitingReply = false;
        endReply(replyKey, this.replyNotice(replied));
      }
    },
  },
};
</script>

<style scoped lang="scss">
/* ===================== layout ===================== */
.alice-chat {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 300px;
  height: 100%;
  min-height: 0;
  background: var(--vp-white);
  transition: grid-template-columns 0.18s ease;
}
.alice-chat__dropzone {
  position: absolute;
  inset: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.85);
  pointer-events: none;
}
.alice-chat__dropzone-inner {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 22px;
  border: 1.5px dashed var(--vp-black);
  background: var(--vp-white);
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 600;
  color: var(--vp-black);
}
.alice-chat--insights-collapsed {
  grid-template-columns: 1fr 0;
}
.alice-chat--insights-collapsed .alice-chat__insights {
  display: none;
}
.alice-chat__insights-toggle {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid var(--border-hairline);
  cursor: pointer;
  color: var(--vp-black);
  margin-left: 8px;
}
.alice-chat__insights-toggle:hover {
  background: var(--bg-muted);
}
.alice-chat__input-area--archived {
  padding: 16px 32px;
}
.alice-chat__archived-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  background: var(--bg-muted);
  border: 1px solid var(--border-hairline);
  font-family: var(--font-ui);
  font-size: 13px;
  color: var(--fg-2);
}
.alice-chat__main {
  position: relative; /* anchors the ChatIndexRail overlay */
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  border-right: 1px solid var(--border-hairline);
}

/* ===================== chat header ===================== */
.alice-chat__header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px 32px;
  border-bottom: 1px solid var(--border-hairline);
  flex-shrink: 0;
}
.alice-chat__title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1;
  color: var(--vp-black);
}
.alice-chat__model {
  display: inline-flex;
  align-items: stretch;
  border: 1px solid var(--border-hairline);
}
.alice-chat__model-label {
  display: inline-flex;
  align-items: center;
  padding: 0 12px;
  background: var(--bg-muted);
  border-right: 1px solid var(--border-hairline);
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
}
.alice-chat__model-control {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px 6px 14px;
  background: var(--vp-white);
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  color: var(--vp-black);
}
.alice-chat__model-select {
  appearance: none;
  -webkit-appearance: none;
  border: none;
  background: transparent;
  font: inherit;
  color: inherit;
  padding-right: 4px;
  cursor: pointer;
  outline: none;
}
.alice-chat__model--btn {
  padding: 0;
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.alice-chat__model-menu {
  min-width: 340px;
}
.alice-chat__model-item.v-list-item--active {
  background: var(--bg-muted);
}
.alice-chat__model-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}
.alice-chat__model-name {
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 600;
  color: var(--vp-black);
}
.alice-chat__model-badges {
  display: inline-flex;
  align-items: center;
}
.alice-chat__eco-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
.alice-chat__eco-badge--low {
  color: var(--vp-groen-160);
}
.alice-chat__eco-badge--low .alice-icon {
  color: var(--vp-groen-100);
}
.alice-chat__eco-badge--medium {
  color: var(--vp-geel-160);
}
.alice-chat__eco-badge--medium .alice-icon {
  color: var(--vp-geel-120);
}
.alice-chat__eco-note {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 8px 4px 4px;
  padding: 10px 12px;
  background: var(--vp-groen-10);
}
.alice-chat__eco-note-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--vp-white);
  color: var(--vp-groen-100);
  flex-shrink: 0;
}
.alice-chat__eco-note-text {
  max-width: 260px;
  font-family: var(--font-ui);
  font-size: 12px;
  line-height: 1.45;
  color: var(--vp-groen-160);
}
.alice-chat__eco-note-text a {
  color: var(--vp-blauw-120);
}
.alice-chat__presence {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.alice-chat__presence-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--vp-groen, #1f9d57);
  box-shadow: 0 0 0 3px rgba(31, 157, 87, 0.18);
  flex-shrink: 0;
}
.alice-chat__presence-stack {
  display: inline-flex;
  align-items: center;
}
.alice-chat__presence-avatar {
  outline: 2px solid var(--vp-white);
}
.alice-chat__presence-avatar:not(:first-child) {
  margin-left: -8px;
}
.alice-chat__presence-more {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  padding: 0 6px;
  margin-left: -8px;
  background: var(--bg-muted);
  outline: 2px solid var(--vp-white);
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  color: var(--fg-3);
}
.alice-chat__presence-label {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-3);
  white-space: nowrap;
}
.alice-chat__spacer {
  flex: 1;
}
.alice-chat__clear {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: var(--vp-white);
  border: 1px solid var(--vp-black);
  border-radius: 0;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--vp-black);
  transition: background 0.12s ease, color 0.12s ease;
}
.alice-chat__clear:hover {
  background: var(--vp-black);
  color: var(--vp-white);
}

/* ===================== reply notification toggle ===================== */
.alice-chat__notify {
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: -8px; /* pairs with "Chat options" */
  background: var(--vp-white);
  border: 1px solid var(--border-hairline);
  border-radius: 0;
  cursor: pointer;
  color: var(--fg-2);
  transition: background 0.12s ease, color 0.12s ease;
}
.alice-chat__notify:hover {
  background: var(--bg-muted);
  color: var(--vp-black);
}
.alice-chat__notify--on,
.alice-chat__notify--on:hover {
  background: var(--vp-black);
  border-color: var(--vp-black);
  color: var(--vp-white);
}

/* ===================== chat mode toggle ===================== */
.alice-chat__mode {
  display: inline-flex;
  border: 1px solid var(--border-hairline);
}
.alice-chat__mode-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  background: var(--vp-white);
  border: none;
  border-right: 1px solid var(--border-hairline);
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
  transition: background 0.12s ease, color 0.12s ease;
}
.alice-chat__mode-btn:last-child {
  border-right: none;
}
.alice-chat__mode-btn:hover {
  color: var(--vp-black);
}
.alice-chat__mode-btn--active {
  background: var(--vp-black);
  color: var(--vp-white);
}

/* communal: per-message queue/answer status under the user bubble */
.alice-chat__msg-status {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-3);
}
.alice-chat__msg-status--error {
  color: var(--vp-rood-160);
  text-transform: none;
  letter-spacing: 0;
}

/* communal: "X is typing…" banner above the input */
.alice-chat__typing-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 8px;
  background: var(--bg-muted);
  border: 1px solid var(--border-hairline);
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 600;
  color: var(--fg-2);
}

/* ===================== chat body ===================== */
.alice-chat__body {
  flex: 1;
  overflow-y: auto;
  padding: 24px 32px 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}
/* The bar rail (ChatIndexRail) floats in the left gutter of the messages
   column; give it its own lane so bars and hover cards never sit on text. */
.alice-chat--has-index .alice-chat__body {
  padding-left: 80px;
}
/* empty-state greeting, centered in the leftover body space */
.alice-chat__welcome {
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  max-width: 420px;
  padding: 24px 0;
  text-align: center;
}
.alice-chat__welcome-mark {
  width: 56px;
  height: 56px;
  padding: 13px;
  border: 1px solid var(--border-hairline);
}
.alice-chat__welcome-title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 24px;
  font-weight: 700;
  line-height: 1.25;
  color: var(--vp-black);
}
.alice-chat__welcome-sub {
  margin: 0;
  font-family: var(--font-body);
  font-size: 14px;
  color: var(--fg-2);
  line-height: 1.5;
}

/* files added to context */
.alice-chat__context {
  border: 1px solid var(--border-hairline);
  background: var(--vp-white);
}
.alice-chat__context-head {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  background: transparent;
  border: 0;
  border-bottom: 1px solid transparent;
  cursor: pointer;
  text-align: left;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
  transition: border-color var(--dur-base) var(--ease-out);
}
.alice-chat__context-head:hover {
  background: var(--bg-2);
}
.alice-chat__context-chevron {
  margin-left: auto;
  transform-origin: center;
  transition: transform var(--dur-base) var(--ease-out);
}
/* Divider under the head fades in with the reveal instead of snapping. */
.alice-chat__context--open .alice-chat__context-head {
  border-bottom-color: var(--border-hairline);
}
.alice-chat__context--open .alice-chat__context-chevron {
  transform: rotate(90deg);
}
/* Collapse wrapper: animate height via the grid-rows 0fr→1fr technique so a
   variable-height list reveals smoothly. Enter eases open; exit is snappier. */
.alice-chat__context-collapse {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows var(--dur-exit) var(--ease-out);
}
.alice-chat__context--open .alice-chat__context-collapse {
  grid-template-rows: 1fr;
  transition: grid-template-rows var(--dur-base) var(--ease-out);
}
.alice-chat__context-clip {
  overflow: hidden;
  min-height: 0;
}
.alice-chat__context-list {
  display: flex;
  flex-direction: column;
  padding: 6px 0;
  opacity: 0;
  transform: translateY(-4px);
  transition: opacity var(--dur-exit) var(--ease-out),
    transform var(--dur-exit) var(--ease-out);
}
.alice-chat__context--open .alice-chat__context-list {
  opacity: 1;
  transform: translateY(0);
  transition: opacity var(--dur-base) var(--ease-out),
    transform var(--dur-base) var(--ease-out);
}
/* Reduced motion: reveal instantly, no movement. */
@media (prefers-reduced-motion: reduce) {
  .alice-chat__context-head,
  .alice-chat__context-chevron,
  .alice-chat__context-collapse,
  .alice-chat__context--open .alice-chat__context-collapse,
  .alice-chat__context-list,
  .alice-chat__context--open .alice-chat__context-list {
    transition: none;
  }
  .alice-chat__context-list {
    transform: none;
  }
}
.alice-chat__context-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px 10px;
}
.alice-chat__context-bulk {
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-3);
}
.alice-chat__context-bulk:hover:not(:disabled) {
  color: var(--vp-black);
}
.alice-chat__context-bulk:disabled {
  opacity: 0.4;
  cursor: default;
}
.alice-chat__context-bulk-sep {
  color: var(--vp-grijs-40);
}
.alice-chat__context-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 16px;
  cursor: pointer;
  font-family: var(--font-mono);
  font-size: 13px;
  color: var(--fg-1);
  min-width: 0;
}
.alice-chat__context-item:hover {
  background: var(--bg-2);
}
.alice-chat__context-check {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
  accent-color: var(--vp-black);
  cursor: pointer;
}
.alice-chat__context-name {
  flex-shrink: 1;
  min-width: 40px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.alice-chat__context-tags {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.alice-chat__context-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 6px;
  background: var(--vp-grijs-20);
  font-family: var(--font-ui);
  font-size: 10px;
  font-weight: 600;
  color: var(--fg-2);
  white-space: nowrap;
}
.alice-chat__context-tag-key {
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-3);
}
.alice-chat__context-item--off .alice-chat__context-tag {
  opacity: 0.5;
}
.alice-chat__context-type {
  margin-left: auto;
  flex-shrink: 0;
  font-family: var(--font-ui);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
}
.alice-chat__context-item--off .alice-chat__context-name {
  color: var(--fg-3);
  text-decoration: line-through;
}

/* status / loading */
.alice-chat__status {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-ui);
  font-size: 13px;
  color: var(--fg-3);
}
.alice-chat__status--error {
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  color: var(--vp-rood-160);
}
.alice-chat__status-dot {
  width: 8px;
  height: 8px;
  background: var(--accent-100);
  animation: alice-pulse 1.2s ease-in-out infinite;
}
@keyframes alice-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

/* messages */
.alice-chat__msg {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 720px;
}
.alice-chat__msg--user {
  align-self: flex-end;
  align-items: flex-end;
}
.alice-chat__msg--ai {
  align-self: flex-start;
  align-items: flex-start;
  width: 100%;
}
.alice-chat__msg-attr {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
}
.alice-chat__msg-time {
  color: var(--fg-3);
}
.alice-chat__msg-name {
  color: var(--fg-2);
}
.alice-chat__msg-mark {
  width: 18px;
  height: 18px;
  background: var(--vp-black);
  flex-shrink: 0;
}
.alice-chat__bubble--user {
  background: var(--vp-black);
  color: var(--vp-white);
  padding: 14px 18px;
  font-family: var(--font-body);
  font-size: 16px;
  line-height: 1.5;
  border-radius: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

/* AI rendered markdown */
.alice-chat__ai {
  font-family: var(--font-body);
  font-size: 16px;
  line-height: 1.6;
  color: var(--vp-black);
  width: 100%;
  max-width: 720px;
}
.alice-chat__ai--pending {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  color: var(--fg-3);
}
.alice-chat__msg--pending {
  opacity: 0.85;
}
.alice-chat__ai :deep(p) {
  margin: 0 0 10px;
}
.alice-chat__ai :deep(p:last-child) {
  margin-bottom: 0;
}
.alice-chat__ai :deep(strong) {
  font-weight: 700;
}
.alice-chat__ai :deep(em) {
  font-style: italic;
}
.alice-chat__ai :deep(ol) {
  margin: 8px 0 12px;
  padding-left: 24px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.alice-chat__ai :deep(ol li) {
  padding-left: 4px;
}
.alice-chat__ai :deep(blockquote) {
  margin: 12px 0;
  padding: 14px 18px;
  background: var(--accent-20);
  border-left: 3px solid var(--accent-100);
  font-style: italic;
  font-family: var(--font-body);
  border-radius: 0;
}
.alice-chat__ai :deep(blockquote p) {
  margin: 0 0 6px;
}
.alice-chat__ai :deep(h1),
.alice-chat__ai :deep(h2),
.alice-chat__ai :deep(h3),
.alice-chat__ai :deep(h4),
.alice-chat__ai :deep(h5),
.alice-chat__ai :deep(h6) {
  font-family: var(--font-ui);
  font-weight: 700;
  line-height: 1.3;
  color: var(--vp-black);
  margin: 18px 0 8px;
}
.alice-chat__ai :deep(h1:first-child),
.alice-chat__ai :deep(h2:first-child),
.alice-chat__ai :deep(h3:first-child),
.alice-chat__ai :deep(h4:first-child),
.alice-chat__ai :deep(h5:first-child),
.alice-chat__ai :deep(h6:first-child) {
  margin-top: 0;
}
.alice-chat__ai :deep(h1) {
  font-size: 22px;
}
.alice-chat__ai :deep(h2) {
  font-size: 19px;
}
.alice-chat__ai :deep(h3) {
  font-size: 17px;
}
.alice-chat__ai :deep(h4),
.alice-chat__ai :deep(h5),
.alice-chat__ai :deep(h6) {
  font-size: 16px;
}

/* Tables (GFM) — wrapped so wide tables scroll instead of overflowing. */
.alice-chat__ai :deep(.alice-chat__table-wrap) {
  margin: 12px 0;
  overflow-x: auto;
  max-width: 100%;
}
.alice-chat__ai :deep(table) {
  border-collapse: collapse;
  width: 100%;
  font-size: 14px;
  line-height: 1.45;
}
.alice-chat__ai :deep(th),
.alice-chat__ai :deep(td) {
  border: 1px solid var(--vp-grijs-60, #d9d9d9);
  padding: 8px 12px;
  text-align: left;
  vertical-align: top;
}
.alice-chat__ai :deep(th) {
  background: var(--vp-grijs-20, #f2f2f2);
  font-family: var(--font-ui);
  font-weight: 700;
  white-space: nowrap;
}
.alice-chat__ai :deep(tbody tr:nth-child(even) td) {
  background: var(--vp-grijs-10, #f9f9f9);
}

.alice-chat__add-insight-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--fg-3);
  border-radius: 0;
}
.alice-chat__add-insight-link:hover {
  color: var(--vp-black);
}
.alice-chat__msg-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}
.alice-chat__selection-save {
  position: fixed;
  z-index: 50;
  transform: translate(-50%, -100%);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: var(--vp-black);
  color: var(--vp-white);
  border: none;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  white-space: nowrap;
}
.alice-chat__selection-save:hover {
  background: var(--vp-grijs-160, #333);
}

/* ===================== inline notice ===================== */
.alice-chat__notice {
  align-self: flex-start;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  max-width: 720px;
  padding: 12px 16px;
  border: 1px solid var(--vp-geel-160);
  background: var(--vp-geel-20);
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.45;
  color: var(--vp-black);
}
.alice-chat__notice-mark {
  width: 8px;
  height: 8px;
  margin-top: 6px;
  background: var(--vp-geel-160);
  flex-shrink: 0;
}
.alice-chat__notice-text {
  flex: 1;
}
.alice-chat__notice-undo {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  height: 24px;
  padding: 0 10px;
  background: transparent;
  border: 1px solid var(--border-hairline);
  cursor: pointer;
  color: var(--fg-1);
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border-radius: 0;
  white-space: nowrap;
}
.alice-chat__notice-undo:hover {
  background: var(--bg-muted);
}
.alice-chat__notice-x {
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--fg-3);
  font-family: var(--font-display);
  font-size: 18px;
  line-height: 1;
  flex-shrink: 0;
}
.alice-chat__notice-x:hover {
  color: var(--vp-black);
}

/* ===================== input ===================== */
.alice-chat__input-area {
  padding: 16px 32px 20px;
  background: var(--vp-white);
  border-top: 1px solid var(--border-hairline);
  flex-shrink: 0;
}
.alice-chat__input-bar {
  position: relative; /* anchors the "/" skill picker popover */
  display: flex;
  align-items: stretch;
  gap: 0;
  border: 1px solid var(--border-hairline);
  background: var(--vp-white);
}
.alice-chat__input-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  border-right: 1px solid var(--border-hairline);
  color: var(--fg-3);
}
.alice-chat__textarea {
  flex: 1;
  padding: 14px 16px;
  border: none;
  outline: none;
  resize: none;
  font-family: var(--font-body);
  font-size: 15px;
  line-height: 1.5;
  color: var(--vp-black);
  background: transparent;
  min-height: 48px;
  max-height: 160px;
}
.alice-chat__textarea::placeholder {
  color: var(--fg-3);
}
.alice-chat__send {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 0 20px;
  background: var(--vp-black);
  color: var(--vp-white);
  border: none;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-radius: 0;
  transition: background 0.15s ease, color 0.15s ease;
}
.alice-chat__send:hover:not(:disabled) {
  background: var(--accent-100);
  color: var(--vp-black);
}
.alice-chat__send:disabled {
  opacity: 0.5;
  cursor: default;
}
.alice-chat__input-helper {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
}
.alice-chat__shortcut {
  font-family: var(--font-mono);
}

/* ===================== attachments ===================== */
.alice-chat__file-input {
  display: none;
}
.alice-chat__pending {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}
.alice-chat__pending-chip {
  position: relative;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px 4px 8px;
  border: 1px solid var(--border-hairline);
  background: var(--vp-white);
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--vp-black);
}
.alice-chat__pending-chip--uploading {
  color: var(--fg-3);
}
.alice-chat__pending-chip-progress {
  position: absolute;
  left: 0;
  bottom: 0;
  height: 2px;
  background: var(--vp-black);
  transition: width 0.18s linear;
}
.alice-chat__pending-chip-thumb {
  width: 28px;
  height: 28px;
  object-fit: cover;
  border-radius: 3px;
  flex-shrink: 0;
}
.alice-chat__pending-chip-name {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.alice-chat__pending-chip-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--fg-3);
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
}
.alice-chat__pending-chip-x:hover {
  color: var(--vp-black);
}

/* ===================== skills (chips, "/" picker, badge) ===================== */
.alice-chat__skills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}
.alice-chat__skill-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px 4px 8px;
  border: 1px solid var(--vp-blauw-40);
  background: var(--vp-blauw-10);
  color: var(--vp-blauw-160);
  font-family: var(--font-ui);
  font-size: 12px;
}
.alice-chat__skill-chip-name {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.alice-chat__skill-chip-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--vp-blauw-160);
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  opacity: 0.6;
}
.alice-chat__skill-chip-x:hover {
  opacity: 1;
}

.alice-chat__slash {
  position: absolute;
  left: -1px;
  right: -1px;
  bottom: calc(100% + 8px);
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid var(--border-hairline);
  background: var(--vp-white);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 20;
}
.alice-chat__slash-empty {
  padding: 12px 16px;
  font-family: var(--font-ui);
  font-size: 13px;
  color: var(--fg-3);
}
.alice-chat__slash-item {
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 10px;
  row-gap: 2px;
  align-items: baseline;
  width: 100%;
  padding: 10px 16px;
  border: none;
  border-bottom: 1px solid var(--border-hairline);
  background: transparent;
  text-align: left;
  cursor: pointer;
}
.alice-chat__slash-item:last-child {
  border-bottom: none;
}
.alice-chat__slash-item.is-active {
  background: var(--vp-blauw-10);
}
.alice-chat__slash-title {
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  color: var(--vp-black);
}
.alice-chat__slash-name {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--vp-blauw-160);
}
.alice-chat__slash-desc {
  grid-column: 1 / -1;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.alice-chat__msg-skill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 6px;
  border: 1px solid var(--vp-blauw-40);
  background: var(--vp-blauw-10);
  color: var(--vp-blauw-160);
  font-size: 10px;
  letter-spacing: 0.06em;
}
.alice-chat__msg-research {
  cursor: pointer;
  font-family: inherit;
  line-height: inherit;
}
.alice-chat__msg-research:hover,
.alice-chat__msg-research--open {
  background: var(--vp-blauw-40);
}
.alice-chat__msg-sources {
  list-style: none;
  margin: 4px 0 10px;
  padding: 6px 10px;
  border-left: 2px solid var(--vp-blauw-40);
  font-family: var(--font-ui);
  font-size: 12px;
}
.alice-chat__msg-sources li {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 2px 0;
}
.alice-chat__msg-sources a {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: var(--vp-blauw-160);
  text-decoration: none;
}
.alice-chat__msg-sources a:hover {
  text-decoration: underline;
}
.alice-chat__msg-source-meta {
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-3);
}
.alice-chat__attach {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  border: none;
  border-right: 1px solid var(--border-hairline);
  background: transparent;
  color: var(--fg-3);
  cursor: pointer;
  transition: color 0.15s ease;
}
.alice-chat__attach:hover:not(:disabled) {
  color: var(--vp-black);
}
.alice-chat__attach:disabled {
  opacity: 0.5;
  cursor: default;
}
.alice-chat__attach--recording {
  color: var(--vp-rood-120);
}
.alice-chat__attach--recording:hover {
  color: var(--vp-rood-100);
}
.alice-chat__rec {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border-right: 1px solid var(--border-hairline);
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--vp-rood-120);
  white-space: nowrap;
}
.alice-chat__rec-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--vp-rood-100);
  animation: alice-rec-pulse 1.2s ease-in-out infinite;
}
@keyframes alice-rec-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.25;
  }
}
.alice-chat__rec-time {
  font-variant-numeric: tabular-nums;
  color: var(--vp-black);
}
.alice-chat__rec-x {
  border: none;
  background: transparent;
  color: var(--fg-3);
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  padding: 0 2px;
}
.alice-chat__rec-x:hover {
  color: var(--vp-black);
}
.alice-chat__msg-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}
.alice-chat__msg-attachment {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  border: 1px solid var(--border-hairline);
  background: var(--vp-white);
  font-family: var(--font-ui);
  font-size: 11px;
  color: var(--vp-black);
  text-decoration: none;
  max-width: 220px;
}
.alice-chat__msg-attachment span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.alice-chat__msg-attachment:hover {
  border-color: var(--vp-black);
}

/* ===================== insights panel ===================== */
.alice-chat__insights {
  position: relative;
  background: var(--vp-white);
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.alice-chat__insights-pattern {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.alice-chat__insights-inner {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  padding: 24px 24px 20px;
  gap: 12px;
}
.alice-chat__insights-head {
  display: flex;
  align-items: center;
  gap: 12px;
}
.alice-chat__insights-title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1;
  color: var(--vp-black);
}
.alice-chat__insights-spacer {
  flex: 1;
}
.alice-chat__panel-tabs {
  display: inline-flex;
  gap: 4px;
}
.alice-chat__panel-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 2px;
  margin-right: 8px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1;
  color: var(--fg-3);
  transition: color 0.12s ease, border-color 0.12s ease;
}
.alice-chat__panel-tab:hover {
  color: var(--vp-black);
}
.alice-chat__panel-tab--active {
  color: var(--vp-black);
  border-bottom-color: var(--vp-black);
}
.alice-chat__saved-panel {
  margin-top: 4px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 2px;
}
.alice-chat__see-all-notes {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding: 4px 0;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--fg-3);
  transition: color 0.12s ease;
}
.alice-chat__see-all-notes:hover {
  color: var(--vp-black);
}
.alice-chat__insights-add {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--vp-white);
  border: 1px solid var(--vp-black);
  border-radius: 0;
  cursor: pointer;
  color: var(--vp-black);
  transition: background 0.12s ease, color 0.12s ease;
}
.alice-chat__insights-add:hover {
  background: var(--vp-black);
  color: var(--vp-white);
}

.alice-chat__insights-meta {
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-3);
}

.alice-chat__insights-add--active {
  background: var(--vp-black);
  color: var(--vp-white);
}

.alice-chat__chip-tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 16px;
  padding: 0 4px;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  color: var(--fg-3);
  background: var(--bg-muted);
}

/* Composer */
.alice-chat__composer {
  display: flex;
  align-items: stretch;
  gap: 0;
  border: 1px solid var(--vp-black);
  background: var(--vp-white);
  margin-top: 4px;
}
.alice-chat__composer-input {
  flex: 1;
  min-width: 0;
  padding: 8px 12px;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-body);
  font-size: 14px;
  color: var(--vp-black);
}
.alice-chat__composer-input::placeholder {
  color: var(--fg-3);
}
.alice-chat__composer-create {
  padding: 0 14px;
  background: var(--vp-black);
  color: var(--vp-white);
  border: none;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-radius: 0;
  transition: background 0.15s ease, color 0.15s ease;
}
.alice-chat__composer-create:hover:not(:disabled) {
  background: var(--accent-100);
  color: var(--vp-black);
}
.alice-chat__composer-create:disabled {
  opacity: 0.5;
  cursor: default;
}
.alice-chat__composer-cancel {
  width: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-left: 1px solid var(--border-hairline);
  background: var(--vp-white);
  cursor: pointer;
  font-family: var(--font-display);
  font-size: 18px;
  line-height: 1;
  color: var(--fg-3);
  border-radius: 0;
}
.alice-chat__composer-cancel:hover {
  color: var(--vp-black);
  background: var(--bg-muted);
}
/* Inline new-chapter form under an AI answer's actions row; the AI message
   article uses align-items: flex-start, which would shrink it to content. */
.alice-chat__composer--msg {
  align-self: stretch;
  max-width: 480px;
}

.alice-chat__insights-list {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-right: 2px;
}

.alice-chat__add-from-chat {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px;
  background: var(--vp-white);
  border: 1px dashed var(--vp-grijs-60);
  border-radius: 0;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-2);
  transition: border-color 0.15s ease, color 0.15s ease;
  margin-top: auto;
}
.alice-chat__add-from-chat:hover:not(:disabled) {
  border-color: var(--vp-black);
  border-style: solid;
  color: var(--vp-black);
}
.alice-chat__add-from-chat:disabled {
  opacity: 0.5;
  cursor: default;
}

@media (max-width: 1279px) {
  .alice-chat {
    grid-template-columns: 1fr 260px;
  }
  .alice-chat--insights-collapsed {
    grid-template-columns: 1fr 0;
  }
  .alice-chat__header,
  .alice-chat__body,
  .alice-chat__input-area {
    padding-left: 20px;
  }
  .alice-chat--has-index .alice-chat__body {
    padding-left: 64px;
    padding-right: 20px;
  }
}
</style>
